import { useEffect, useRef, useState } from 'react';
import { bt, btp } from '../i18n';
import { dashboardExitUrl, derivePreviewUrl, deriveStorefrontUrl } from '../boot/config';
import { useBuilderBreakpoint } from '../hooks/useMediaQuery';
import {
  isThemeWorkspaceHandle,
  themeIdOfWorkspaceHandle,
  useBuilderStore,
  type DevicePreview,
  // Aliased: the local `TemplateEntry` below is a PAGE TYPE (Home/Product/Collection…), a different
  // concept from a named template of one type — same word, two layers.
  type NewPageSource,
  type SaveFailure,
  type TemplateEntry as NamedTemplate,
} from '../state/builderStore';
import { getThemeListing } from '@aquilixshop/ui-react';
import { isReadOnlyView } from '../boot/view-handoff';
import { ReadOnlyViewError } from '../lib/errors';
import { builderApi, type CustomPageSummary } from '../lib/builderApi';
import { useChromeStore, type SidebarTab } from '../state/chromeStore';
import { publish, saveDraftNow } from '../state/autosave';
import { SaveStateIndicator } from './SaveStateIndicator';
import { UploadTray } from './UploadTray';
import { pushToast } from '../ui/toastStore';
import { announce } from '../ui/liveRegionStore';
import { Modal } from '../ui/Modal';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DesktopIcon,
  EyeIcon,
  InspectorIcon,
  MobileIcon,
  MoreIcon,
  PlusIcon,
  RedoIcon,
  SearchIcon,
  SectionsIcon,
  SettingsIcon,
  StoreIcon,
  templatePageIcon,
  TrashIcon,
  UndoIcon,
  ViewStoreIcon,
} from '../ui/icons';

// Desktop | Mobile only, exactly like Shopify (no tablet).
const DEVICES: ReadonlyArray<{
  value: DevicePreview;
  label: string;
  icon: React.ReactNode;
}> = [
  { value: 'desktop', label: bt('chrome.device.desktop', 'Desktop'), icon: <DesktopIcon /> },
  { value: 'mobile', label: bt('chrome.device.mobile', 'Mobile'), icon: <MobileIcon /> },
];

/**
 * Device-preview toggle (build-plan §4.4, §7.2) — a `role="group"` of 3 `aria-pressed` icon buttons
 * that frame the canvas at preset widths via CSS. Desktop | (tablet, muted) | mobile, Shopify-style.
 */
function DeviceToggle() {
  const device = useBuilderStore((s) => s.device);
  const setDevice = useBuilderStore((s) => s.setDevice);
  return (
    <div
      role="group"
      aria-label={bt('chrome.device.aria', 'Device preview')}
      className="bld-device"
    >
      {DEVICES.map(({ value, label, icon }) => {
        const active = device === value;
        return (
          <button
            key={value}
            type="button"
            className="bld-device__btn"
            aria-pressed={active}
            aria-label={label}
            title={label}
            onClick={() => {
              setDevice(value);
              announce(
                btp('chrome.device.previewing_sr', 'Previewing on {device}.', {
                  device: label.toLowerCase(),
                }),
              );
            }}
          >
            {icon}
          </button>
        );
      })}
    </div>
  );
}

const TABS: ReadonlyArray<{ value: SidebarTab; label: string; icon: React.ReactNode }> = [
  { value: 'sections', label: bt('chrome.tabs.sections', 'Sections'), icon: <SectionsIcon /> },
  { value: 'theme', label: bt('chrome.tabs.theme', 'Theme settings'), icon: <SettingsIcon /> },
];

/**
 * LEFT icon-rail (chrome feature 1) — toggle buttons that switch what the left sidebar shows.
 * Clicking the active one collapses the sidebar. Inert spans before; now wired to `chromeStore`.
 */
function SidebarTabs() {
  const sidebarTab = useChromeStore((s) => s.sidebarTab);
  const collapsed = useChromeStore((s) => s.sidebarCollapsed);
  const selectSidebarTab = useChromeStore((s) => s.selectSidebarTab);
  return (
    <div
      className="bld-iconrail"
      role="group"
      aria-label={bt('chrome.tabs.aria', 'Sidebar panels')}
    >
      {TABS.map(({ value, label, icon }) => {
        const active = !collapsed && sidebarTab === value;
        return (
          <button
            key={value}
            type="button"
            className="bld-iconrail__btn"
            aria-pressed={active}
            aria-label={label}
            title={label}
            onClick={() => selectSidebarTab(value)}
          >
            {icon}
          </button>
        );
      })}
    </div>
  );
}

/**
 * CENTER page/template selector — a dropdown button with a search field inside (Shopify parity).
 *
 * The DEFAULT THEME seeds two complete documents (Home + Product); choosing either loads that template's
 * draft via the store's `load({template})` (the same path the URL params drive). The remaining templates
 * are listed for Shopify parity but not yet seeded, so they're shown disabled ("Coming soon"). No engine
 * change — switching is just another `load(route)`.
 */
interface TemplateEntry {
  /** Display label shown in the menu + as the current page name. */
  label: string;
  /** TemplateKey passed to load(); undefined = not seeded yet (listed but disabled). */
  template?: string;
}

/**
 * LA FENÊTRE DE BIENVENUE EST-ELLE OFFERTE DANS LE MENU DES PAGES ? (0074, 18/10/2026 au soir)
 *
 * Non, et c'est une décision de la fondatrice, pas un chantier inachevé : « pour l'instant juste cache
 * cette page dans le builder, on l'affiche pas jusqu'à ce que je décide ».
 *
 * Ce qu'elle décide : où le DESIGN de la fenêtre se règle. Il existe aujourd'hui à deux endroits — onze
 * réglages (textes, couleurs, arrondi, image, mise en page) vivent à la fois dans l'écran « Bienvenue »
 * de l'app Courriels de marque et dans ce gabarit. Les deux écrivent dans des tiroirs différents et ne
 * se parlent pas, et la vitrine ne lit que le premier. Offrir les deux portes en même temps, c'est
 * promettre à une vendeuse que son dessin part en ligne alors qu'il ne part nulle part.
 *
 * **Rien n'est supprimé.** Le gabarit, sa section, ses blocs, sa graine et son parcours sont entiers :
 * `/?template=welcome-popup` l'ouvre toujours, et c'est par là que le parcours Playwright le marche. Le
 * jour où elle tranche, cette constante passe à `true` et la porte se rouvre.
 */
const SHOW_WELCOME_POPUP = false;

/**
 * The page list, built ON EVERY CALL — never a module-level constant (22/09/2026).
 *
 * `bt()` reads the dictionary chunk that `main.tsx` awaits before the first render. A module-level array
 * evaluates while the module graph is still being imported, which is BEFORE that await resolves: every
 * label in this menu was therefore resolved against an empty registry and fell back to its English floor,
 * in every language. Nobody noticed because the words that gave it away read the same in both — the page
 * called "Collections" in English is "Collections" in French too. The thank-you page's walk is what
 * finally caught it, with a seller reading « Thank-you page » in a French editor.
 */
function templateEntries(): ReadonlyArray<TemplateEntry> {
  return [
    { label: bt('chrome.tpl.home', 'Home page'), template: 'index' },
    { label: bt('chrome.tpl.product', 'Product'), template: 'product' },
    { label: bt('chrome.tpl.collection', 'Collection'), template: 'collection' },
    // The /collections LIST page (22/09/2026) — before this entry the storefront painted it from code and the
    // seller had no control that reached it.
    { label: bt('chrome.tpl.collections', 'Collections'), template: 'collections' },
    { label: bt('chrome.tpl.pages', 'Pages'), template: 'page' },
    { label: bt('chrome.tpl.cart', 'Cart'), template: 'cart' },
    // The order THANK-YOU page (22/09/2026) — before this entry the storefront painted it from code and the
    // seller had no control that reached the last screen a buyer ever sees.
    { label: bt('chrome.tpl.thank_you', 'Thank-you page'), template: 'thank-you' },
    // The CHECKOUT page (05/10/2026) — the storefront painted it from code in the THEME's colours, so a
    // dark theme paid on a page it could not read, and no seller could change one colour of it.
    { label: bt('chrome.tpl.checkout', 'Checkout'), template: 'checkout' },
    // La FENÊTRE DE BIENVENUE n'est offerte ici QUE si {@link SHOW_WELCOME_POPUP} le dit — et il dit
    // non depuis le 18/10/2026 au soir. Voir la note sur la constante : la porte est fermée, le gabarit
    // est entier derrière.
    ...(SHOW_WELCOME_POPUP
      ? [{ label: bt('chrome.tpl.welcome_popup', 'Welcome popup'), template: 'welcome-popup' }]
      : []),
    { label: bt('chrome.tpl.search', 'Search') },
    { label: bt('chrome.tpl.not_found', '404 page') },
  ];
}

/**
 * Templates that carry NAMED theme variants (a per-product/collection theme template — feature 0018). NOTE:
 * `page` is NOT here — custom CMS pages (feature 0027) are standalone documents keyed by their own slug, not
 * theme-template variants, so they get their own drill submenu (see the `page` PanelView), not the named-
 * template create/duplicate form.
 */
const NAMED_TEMPLATE_TYPES = new Set(['product', 'collection']);

/**
 * Turn whatever the seller typed into a storable handle suffix (kebab-case): accents stripped, lower-
 * cased, every run of anything else (spaces, underscores, punctuation) folded into one hyphen, edges
 * trimmed. « mivexProd » → `mivexprod`, « Mon Produit été » → `mon-produit-ete`. The seller never has to
 * know the rule — the form shows the resulting name instead of refusing the input (11/09).
 */
export function toHandle(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Validate a new handle suffix (non-blank once normalised, not the reserved base, unique, capped). 0018. */
function validateHandle(raw: string, existing: string[]): string | null {
  if (!raw.trim()) return bt('chrome.tpl.err_name', 'Enter a name');
  const h = toHandle(raw);
  if (!h) return bt('chrome.tpl.err_charset', 'Use at least one letter (a–z) or digit');
  if (h === 'default') return '“default” is the base template — pick another name';
  if (existing.includes(h)) return 'A template with that name already exists';
  if (existing.filter((x) => x !== 'default').length >= 20)
    return bt('chrome.tpl.err_limit', 'Limit of 20 templates per type reached');
  return null;
}

/** Which in-panel view the merged selector is showing (0021; `page` added 0027). */
type PanelView = 'root' | 'product' | 'collection' | 'products' | 'collections' | 'page';
/**
 * Page types that drill to a submenu in the selector: product/collection own named theme templates (0021);
 * `page` (0027) drills to the custom-pages manager (list + New page). Unlike product/collection, `page` has
 * NO `default` document — it is only ever opened by picking a concrete page (or creating one).
 */
const DRILLABLE_TYPES = new Set(['product', 'collection', 'page']);
/** Main page types whose default drafts we prefetch so switching between them is instant (0021). */
const MAIN_TEMPLATES = [
  'index',
  'product',
  'collection',
  'collections',
  'cart',
  'thank-you',
  'checkout',
  // `welcome-popup` n'est PAS ici : on ne préfetche pas le brouillon d'une page que le menu n'offre
  // pas. Il revient avec elle le jour où {@link SHOW_WELCOME_POPUP} repasse à `true`.
] as const;

/** The key identifying a "start from" choice in the New-page form — `null` = the blank starter. (0027) */
function sourceKey(source: NewPageSource | null): string {
  return source ? `${source.template}:${source.handle}` : 'blank';
}

function PageSelector() {
  const [open, setOpen] = useState(false);
  // The drill view. Opening lands on the current type's submenu when it's drillable (so tweaking
  // layout/product is one click), else the root page list.
  const [view, setView] = useState<PanelView>('root');
  const [query, setQuery] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [collectionQuery, setCollectionQuery] = useState('');
  // 0018 named-template manager state: the discovered templates (handle + publish state) + the inline
  // create/duplicate form. Publish state is carried because an unpublished template cannot be assigned.
  const [entries, setEntries] = useState<NamedTemplate[]>([]);
  // The Layout list's own load state. Without it a slow fetch (the preprod store_page query has been
  // measured at 3.5s) renders as an empty list, and a FAILED one used to render as "default + the open
  // handle" — a short list that looks like the truth. Both read as "your other layouts are gone".
  const [entriesState, setEntriesState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [templatesNonce, setTemplatesNonce] = useState(0);
  // The create dialog (19/09/2026). One door, two fields — a name and the template it copies. It replaced
  // a pair of buttons ("+ New template" / "Duplicate X") that offered a choice the seller never wanted to
  // make: the blank one started from the theme's raw seed, a page she had never seen, and the duplicate
  // could only ever copy the template that happened to be open.
  // 'assign' (20/09/2026) is the confirm step in front of the apply write: the founder lost an afternoon
  // to a layout she believed was applied, so the act that binds a product to a template now names BOTH
  // out loud and waits for a yes — "we should specify the product we are applying the template to".
  const [form, setForm] = useState<'none' | 'create' | 'assign'>('none');
  const [name, setName] = useState('');
  const [source, setSource] = useState('default');
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Does the new template go ON the product on the canvas? (26/10/2026 — "j'ai créé un produit, j'ai
  // switché pour ce produit, j'ai créé une nouvelle template […] le produit n'a pas été rattaché à la
  // template. Un autre seller va croire que c'est lié, et il commence le travail et il trouve qu'il touche
  // à une autre template.") Creating a layout while a product is on screen used to bind nothing: the
  // product kept rendering `default`, the canvas kept showing the product, and the only contradiction was
  // a grey "No products" pill. Checked by default, because "a layout for THIS product" is what the gesture
  // means; the box is there so the other intent — a layout to assign later — stays one click away.
  const [attachToProduct, setAttachToProduct] = useState(true);
  // The "apply this template to the previewed product" action's in-flight + error state.
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  // Publishing the OPEN template from right here. The Publish button lives at the far end of the toolbar
  // and reads as "publish my page", so a seller told "publish this template before you can assign it" had
  // to guess that the two were the same act. The step is offered where the requirement is stated.
  const [publishingTpl, setPublishingTpl] = useState(false);
  const [publishTplError, setPublishTplError] = useState<string | null>(null);
  const [bindingId, setBindingId] = useState<string | null>(null);
  // Why a VISIBLE error and not just `announce()`: binding a preview product is one fetch away from
  // failing (backend down, product deleted), and the only feedback used to be the polite live region —
  // silent to a sighted seller. The popover stayed open, unchanged, so a failed pick was indistinguishable
  // from a dead button. It cost the founder a "the picker isn't clickable" bug report; the picker was fine.
  const [pickError, setPickError] = useState<string | null>(null);
  // The collection being bound into the preview ('__demo__' for the sample) + the last binding failure.
  const [bindingCollectionId, setBindingCollectionId] = useState<string | null>(null);
  const [pickCollectionError, setPickCollectionError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  // 0027 custom-pages manager state: the discovered pages, the inline "new page" form, and per-row rename.
  const [pages, setPages] = useState<CustomPageSummary[]>([]);
  const [pageFormOpen, setPageFormOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState('');
  // "Start from" — the page the new one is built ON TOP of. `null` = the blank starter (the default).
  const [pageSource, setPageSource] = useState<NewPageSource | null>(null);
  const [renamingHandle, setRenamingHandle] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const load = useBuilderStore((s) => s.load);
  const selectTemplate = useBuilderStore((s) => s.selectTemplate);
  const createTemplate = useBuilderStore((s) => s.createTemplate);
  const listTemplateEntries = useBuilderStore((s) => s.listTemplateEntries);
  const loadTemplateUsage = useBuilderStore((s) => s.loadTemplateUsage);
  const assignTemplateToPreviewProduct = useBuilderStore((s) => s.assignTemplateToPreviewProduct);
  const assignTemplateToProduct = useBuilderStore((s) => s.assignTemplateToProduct);
  const templateUsage = useBuilderStore((s) => s.templateUsage);
  const previewProductTemplate = useBuilderStore((s) => s.previewProductTemplate);
  const previewProductTemplateKnown = useBuilderStore((s) => s.previewProductTemplateKnown);
  // The OPEN document's own publish state — the authority on the open template while the server's list is
  // still in flight (see `currentTemplatePublished`).
  const publishedAt = useBuilderStore((s) => s.publishedAt);
  const listPages = useBuilderStore((s) => s.listPages);
  const createPage = useBuilderStore((s) => s.createPage);
  const renamePage = useBuilderStore((s) => s.renamePage);
  const deletePage = useBuilderStore((s) => s.deletePage);
  const loadCatalog = useBuilderStore((s) => s.loadCatalog);
  const prefetchRoute = useBuilderStore((s) => s.prefetchRoute);
  const bindPreviewProduct = useBuilderStore((s) => s.bindPreviewProduct);
  const catalog = useBuilderStore((s) => s.catalog);
  const catalogState = useBuilderStore((s) => s.catalogState);
  const previewProduct = useBuilderStore((s) => s.previewProduct);
  const previewCollection = useBuilderStore((s) => s.previewCollection);
  const previewCollectionState = useBuilderStore((s) => s.previewCollectionState);
  const collectionsCatalog = useBuilderStore((s) => s.collectionsCatalog);
  const collectionsCatalogState = useBuilderStore((s) => s.collectionsCatalogState);
  const loadCollectionsCatalog = useBuilderStore((s) => s.loadCollectionsCatalog);
  const bindPreviewCollection = useBuilderStore((s) => s.bindPreviewCollection);
  const currentTemplate = useBuilderStore((s) => s.route.template);
  const currentHandle = useBuilderStore((s) => s.route.handle);
  // Superadmin consultation: template create/duplicate/apply/publish are greyed, not just refused.
  const readOnlyView = isReadOnlyView();
  const locale = useBuilderStore((s) => s.route.locale);

  const named = NAMED_TEMPLATE_TYPES.has(currentTemplate);
  const drillableNow = DRILLABLE_TYPES.has(currentTemplate);

  // "Start from" choices for a NEW page (0027): the blank starter, the home page, then every page the
  // seller already built. The home entry points at the OPEN home route when one is open — inside a theme
  // workspace that is the workspace's own home, which is the home the seller is actually looking at.
  const pageSourceOptions: ReadonlyArray<{
    key: string;
    label: string;
    icon: React.ReactNode;
    source: NewPageSource | null;
  }> = [
    {
      key: 'blank',
      label: bt('chrome.pages.blank', 'Blank page'),
      icon: templatePageIcon('page', 15),
      source: null,
    },
    {
      key: sourceKey({
        template: 'index',
        handle: currentTemplate === 'index' ? currentHandle : 'default',
      }),
      label: bt('chrome.tpl.home', 'Home page'),
      icon: templatePageIcon('index', 15),
      source: {
        template: 'index',
        handle: currentTemplate === 'index' ? currentHandle : 'default',
      },
    },
    ...pages.map((p) => ({
      key: sourceKey({ template: 'page', handle: p.handle }),
      label: p.title || p.handle,
      icon: templatePageIcon('page', 15),
      source: { template: 'page', handle: p.handle } as NewPageSource,
    })),
  ];

  function close() {
    setOpen(false);
    btnRef.current?.focus();
  }
  function openMenu() {
    // Land on the current type's submenu when it's drillable (product/collection) so changing layout or
    // preview product is immediate; otherwise the root page list.
    setView(drillableNow ? (currentTemplate as PanelView) : 'root');
    setQuery('');
    setProductQuery('');
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Discover the published handles for the current type when its submenu is shown (best-effort). The
  // submenu is only reachable while editing that type, so `currentTemplate` is the right key. 'default' +
  // the active handle are always shown (a freshly-created draft handle isn't published yet). (0018)
  //
  // THEME WORKSPACE: inside one, the workspace draft IS that theme's default page (it BECOMES the live
  // `default` on publish), so the Layout list is exactly ONE row — 'default'. Two rules make that true:
  // `theme-ws-*` is a RESERVED internal handle and must never surface as a seller-facing named template
  // (it used to render raw, as "theme-ws-electronics"), and the LIVE named handles must not be offered
  // either — clicking one would silently leave the theme and open the live (Atlas) page, the same bug
  // `chooseType` already fixed for the root row. Named templates are a live-theme concept.
  useEffect(() => {
    if (!open || !named) return;
    // Computed inline (not from the `inThemeWorkspace` const below) — that one is declared after this
    // effect and would be in its temporal dead zone here.
    if (isThemeWorkspaceHandle(currentHandle)) {
      setEntries([{ handle: 'default', status: 'PUBLISHED', hasUnpublishedChanges: false }]);
      return;
    }
    let alive = true;
    setEntriesState('loading');
    void listTemplateEntries().then((found) => {
      if (!alive) return;
      if (found === null) {
        // The request failed. Leave whatever the list already holds and SAY so — replacing it with the
        // synthesised "default + open handle" pair would quietly assert the store has no other layouts.
        setEntriesState('error');
        return;
      }
      setEntriesState('idle');
      // 'default' and the OPEN handle are always listed even when the server didn't return them (a
      // just-created template has no published row and, until its first save lands, no row at all).
      // `default` is synthesised as PUBLISHED (it is the base page); the OPEN handle takes the open
      // document's OWN answer — `publishedAt` — because a blind DRAFT claimed "publish it before assigning
      // it" about a template the create had just published, and greyed the button that attaches a product
      // (26/10/2026). Still DRAFT when the document has no published row: that assignment WOULD 422.
      const byHandle = new Map(found.map((e) => [e.handle, e]));
      // Set-deduped: on the default page `currentHandle` IS 'default', and listing it twice renders two
      // identical rows (and breaks the radio group's single-selection contract).
      const merged = Array.from(new Set(['default', currentHandle]))
        .filter((h) => !byHandle.has(h))
        .map<NamedTemplate>((h) => ({
          handle: h,
          status:
            h === 'default' || (h === currentHandle && useBuilderStore.getState().publishedAt)
              ? 'PUBLISHED'
              : 'DRAFT',
          hasUnpublishedChanges: h !== 'default',
        }));
      setEntries(
        [...merged, ...found]
          .filter((e) => !isThemeWorkspaceHandle(e.handle))
          .sort((a, b) =>
            a.handle === 'default'
              ? -1
              : b.handle === 'default'
                ? 1
                : a.handle.localeCompare(b.handle),
          ),
      );
    });
    return () => {
      alive = false;
    };
  }, [open, named, currentTemplate, currentHandle, listTemplateEntries, templatesNonce]);

  // Usage counts (how many products render each template) — refreshed after an assignment by the store
  // action itself. Product templates only: only products carry a template assignment, so the number would
  // be meaningless for a collection layout.
  //
  // NOT gated on `open` any more (20/09/2026). The counts used to load only when the popover was opened,
  // which made the one fact that matters — "nothing renders this layout" — reachable only by the seller
  // who already suspected it. The reach pill on the closed switcher and the publish confirm both need the
  // answer before anything is clicked, so it is fetched as soon as a named product template is on screen.
  useEffect(() => {
    if (currentTemplate !== 'product') return;
    void loadTemplateUsage();
  }, [open, currentTemplate, currentHandle, loadTemplateUsage]);

  // Discover the store's custom pages when the "Pages" submenu is shown (feature 0027, best-effort). Kept
  // separate from the named-template discovery: pages are standalone documents (title + slug), not theme
  // variants.
  useEffect(() => {
    if (!open || view !== 'page') return;
    let alive = true;
    void listPages().then((ps) => {
      if (alive) setPages(ps);
    });
    return () => {
      alive = false;
    };
  }, [open, view, listPages]);

  // Warm the shared catalog cache so the "Change product" list opens instantly (0021). Idle-prefetch when
  // this control mounts on a product template (only there — never a boot-blocking GET for a Home editor),
  // and again whenever the product picker becomes reachable. `loadCatalog` is idempotent in the store.
  useEffect(() => {
    if (currentTemplate !== 'product') return;
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback !== 'function') {
      void loadCatalog();
      return;
    }
    const id = w.requestIdleCallback(() => void loadCatalog());
    return () => w.cancelIdleCallback?.(id);
  }, [currentTemplate, loadCatalog]);
  useEffect(() => {
    if (open && (view === 'product' || view === 'products')) void loadCatalog();
  }, [open, view, loadCatalog]);
  // Same warm-up for the collections list, on a collection template (22/09/2026).
  useEffect(() => {
    if (currentTemplate !== 'collection') return;
    void loadCollectionsCatalog();
  }, [currentTemplate, loadCollectionsCatalog]);
  useEffect(() => {
    if (open && (view === 'collection' || view === 'collections')) void loadCollectionsCatalog();
  }, [open, view, loadCollectionsCatalog]);

  // Warm the page cache (0021) so clicking a page switches INSTANTLY: prefetch the main templates' default
  // drafts on idle after mount, and again the moment the switcher opens (belt-and-suspenders for a fast
  // click). prefetchRoute is idempotent + best-effort in the store; the current route is skipped.
  useEffect(() => {
    const curKey = `${currentTemplate}|${currentHandle}|${locale}`;
    // Inside a theme workspace, warm the WORKSPACE routes (that's where the switcher navigates).
    const warmHandle = isThemeWorkspaceHandle(currentHandle) ? currentHandle : 'default';
    const warm = () => {
      for (const t of MAIN_TEMPLATES) {
        if (`${t}|${warmHandle}|${locale}` !== curKey) {
          void prefetchRoute({ template: t, handle: warmHandle, locale });
        }
      }
    };
    if (open) {
      warm();
      return;
    }
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(warm);
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(warm, 1200);
    return () => window.clearTimeout(id);
  }, [open, currentTemplate, currentHandle, locale, prefetchRoute]);

  // Reset the queries + the page form whenever the menu closes. NOT the create dialog: it is portalled
  // to the body and deliberately outlives the popover (opening it closes the menu underneath).
  useEffect(() => {
    if (!open) {
      setQuery('');
      setProductQuery('');
      setPageFormOpen(false);
      setPageTitle('');
      setRenamingHandle(null);
      // NOT assignError: the apply confirm dialog is portalled to the body and outlives the popover
      // (opening it closes the menu underneath), so clearing it here would blank the one place that
      // says why the assignment failed. The dialog clears it on its own open/close.
    }
  }, [open]);

  // THEME WORKSPACE (spec 0032): while editing an applied theme (handle `theme-ws-<id>`), page
  // switching must STAY inside that theme's draft set (see chooseType) and the switcher label shows
  // the theme name, never the technical handle.
  const inThemeWorkspace = isThemeWorkspaceHandle(currentHandle);
  const targetHandle = inThemeWorkspace ? currentHandle : 'default';
  const items = templateEntries().filter((t) =>
    t.label.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const typeLabel =
    templateEntries().find((t) => t.template === currentTemplate)?.label ?? currentTemplate;
  const nameError =
    form === 'none'
      ? null
      : validateHandle(
          name,
          entries.map((e) => e.handle),
        );
  // Show the active handle as a suffix for named templates AND for the currently-open custom page (0027).
  // A theme workspace shows its theme NAME (`· Volt draft`), never the technical handle.
  const suffix = inThemeWorkspace
    ? ` · ${btp('chrome.top.theme_draft', '{name} draft', { name: getThemeListing(themeIdOfWorkspaceHandle(currentHandle))?.name ?? bt('chrome.top.theme_fallback', 'Theme') })}`
    : (named || currentTemplate === 'page') && currentHandle !== 'default'
      ? ` · ${currentHandle}`
      : '';
  const filteredProducts = catalog.filter((p) =>
    p.name.toLowerCase().includes(productQuery.trim().toLowerCase()),
  );
  const filteredCollections = collectionsCatalog.filter((c) =>
    c.name.toLowerCase().includes(collectionQuery.trim().toLowerCase()),
  );
  // What the page-selector button says the collection template is previewing (22/09/2026): the bound
  // collection's name, or the sample while nothing is bound yet.
  const boundCollectionName =
    currentTemplate === 'collection'
      ? (previewCollection?.name ??
        (previewCollectionState === 'loading'
          ? bt('chrome.common.loading', 'loading…')
          : bt('chrome.coll.sample', 'Sample collection')))
      : null;

  // Root row click: single-template types navigate + close; drillable types either browse their submenu
  // (already on that type — the popover stays open) or navigate to the type's default and close (the
  // shell reloads, so keeping the menu open across a load isn't possible). (0021)
  //
  // Navigating to `default` here was the bug where the Volt workspace's Product page showed the LIVE
  // (Atlas) product draft — inside a workspace the target handle IS the workspace. Named-template
  // drilling is a live-theme concept, so workspace navigation goes direct.
  function chooseType(entry: TemplateEntry) {
    if (!entry.template) return;
    const t = entry.template;
    // `page` has NO `default` document — it ALWAYS drills into the pages manager (never a phantom load of
    // `(page, default)`). This must precede the generic drillable branch (feature 0027).
    if (t === 'page') {
      setView('page');
      return;
    }
    if (DRILLABLE_TYPES.has(t) && !inThemeWorkspace) {
      if (t === currentTemplate) {
        setView(t as PanelView);
        return;
      }
      close();
      // "Product" names the page of the product on screen, not a layout: open the one that product renders
      // (founder 05/10/2026 — `default` under her Move Shift was a page it never renders). Not known yet
      // (`null`) opens `default`, and `load` follows the product the moment it is revalidated.
      const handle =
        t === 'product' && previewProduct ? (previewProductTemplate ?? 'default') : 'default';
      void load({ template: t, handle, locale }).catch(() => undefined);
      announce(
        btp('chrome.tpl.now_editing', 'Now editing the {name} template.', { name: entry.label }),
      );
      return;
    }
    close();
    if (t === currentTemplate && currentHandle === targetHandle) return;
    void load({ template: t, handle: targetHandle, locale }).catch(() => undefined);
    announce(
      btp('chrome.tpl.now_editing', 'Now editing the {name} template.', { name: entry.label }),
    );
  }

  // Open a custom page for editing (feature 0027) — a plain navigation like chooseType's leaf case.
  function openPage(handle: string) {
    close();
    if (currentTemplate === 'page' && handle === currentHandle) return;
    void load({ template: 'page', handle, locale }).catch(() => undefined);
    announce(btp('chrome.pages.now_editing', 'Now editing the page {name}.', { name: handle }));
  }

  async function submitNewPage() {
    const title = pageTitle.trim();
    if (!title || busy) return;
    setBusy(true);
    try {
      await createPage(title, pageSource ?? undefined);
      announce(
        pageSource
          ? btp('chrome.pages.created_from', 'Created the page “{title}” from {source}.', {
              title,
              source: sourceLabel(pageSource),
            })
          : btp('chrome.pages.created', 'Created the page “{title}”.', { title }),
      );
      close();
    } catch {
      // `announce` only reaches a screen reader: a sighted seller saw the form close, the list stay
      // empty, and concluded the feature was broken. Failures get a toast like every other write here.
      announce(bt('chrome.pages.create_failed_sr', 'Could not create the page.'));
      pushToast(
        bt('chrome.pages.create_failed', 'Couldn’t create the page. Please try again.'),
        'error',
      );
    } finally {
      setBusy(false);
      setPageFormOpen(false);
      setPageTitle('');
      setPageSource(null);
    }
  }

  /** The human label of a "start from" choice — the page's title, or "Home page" for the home route. */
  function sourceLabel(source: NewPageSource): string {
    if (source.template === 'index') return bt('chrome.pages.the_home', 'the home page');
    const found = pages.find((p) => p.handle === source.handle);
    return `“${found?.title || source.handle}”`;
  }

  async function removePage(handle: string, label: string) {
    const wasCurrent = currentTemplate === 'page' && handle === currentHandle;
    try {
      await deletePage(handle);
      announce(btp('chrome.pages.deleted_sr', 'Deleted the page {name}.', { name: label }));
      if (wasCurrent) {
        close();
        return;
      }
      setPages(await listPages());
    } catch {
      announce(bt('chrome.pages.delete_failed_sr', 'Could not delete the page.'));
      pushToast(
        bt('chrome.pages.delete_failed', 'Couldn’t delete the page. Please try again.'),
        'error',
      );
    }
  }

  async function submitRename(handle: string) {
    const title = renameValue.trim();
    if (!title) return;
    try {
      await renamePage(handle, title);
      setRenamingHandle(null);
      setPages(await listPages());
      announce(btp('chrome.pages.renamed_sr', 'Renamed the page to “{name}”.', { name: title }));
    } catch {
      announce(bt('chrome.pages.rename_failed_sr', 'Could not rename the page.'));
      pushToast(
        bt('chrome.pages.rename_failed', 'Couldn’t rename the page. Please try again.'),
        'error',
      );
    }
  }

  function chooseHandle(handle: string) {
    close();
    if (handle === currentHandle) return;
    // Inside a theme workspace the single 'default' row IS the page already open (the workspace draft
    // becomes the live default on publish), so selecting it must be a no-op — navigating to the literal
    // 'default' handle would drop the seller out of the theme and into the live page.
    if (inThemeWorkspace) return;
    void selectTemplate(handle).catch(() => undefined);
    announce(
      btp('chrome.tpl.now_editing_sr', 'Now editing {type} · {handle}.', {
        type: typeLabel,
        handle,
      }),
    );
  }

  async function submitForm() {
    if (nameError || busy) return;
    const handle = toHandle(name);
    // Read the target BEFORE the create: the product is what the dialog named, not whatever happens to be
    // bound when the two round trips come back.
    const target = canAttachOnCreate && attachToProduct ? previewProduct : null;
    setBusy(true);
    setCreateError(null);
    try {
      // The new template is a COPY of the one named in the second field — there is no blank start.
      await createTemplate(handle, source);
      announce(
        btp('chrome.tpl.created_sr', 'Created {type} template “{handle}”.', {
          type: typeLabel,
          handle,
        }),
      );
      // …and, unless she said otherwise, the product on the canvas now RENDERS it. Separate round trip,
      // separate failure: the template exists either way, so a refused assignment must not read as "the
      // create failed" — it says what was created, what was not joined, and where to finish it.
      if (target) {
        try {
          await assignTemplateToProduct(target.id, handle);
          pushToast(
            btp('chrome.tpl.applied_toast', '“{name}” now renders {product}.', {
              name: handle,
              product: target.name,
            }),
            'success',
          );
        } catch {
          pushToast(
            btp(
              'chrome.tpl.create_attach_failed',
              'Template “{name}” was created, but it could not be applied to {product}. Open the page selector to apply it.',
              { name: handle, product: target.name },
            ),
            'error',
          );
        }
      }
      setForm('none');
      setName('');
      close();
    } catch (e) {
      // Visible, not just announced — same lesson as the product picker: a form that stays open and
      // unchanged after a failed submit reads as a button that does nothing. A read-only consultation
      // is NOT a retryable failure — "Réessayez" would send the admin in circles.
      setCreateError(
        e instanceof ReadOnlyViewError
          ? bt(
              'chrome.view.readonly_write',
              'Read-only consultation — nothing can be changed on this store.',
            )
          : bt('chrome.tpl.create_failed', 'Couldn’t create this template. Try again.'),
      );
      announce(bt('chrome.tpl.create_failed_sr', 'Could not create the template.'));
    } finally {
      setBusy(false);
    }
  }

  // Bind (or clear → demo) the preview product. Non-navigating (you're on the product route when the
  // picker is reachable), so the canvas swaps live and the popover just closes. (0021)
  // Bind a collection into the preview (22/09/2026) — mirrors pickProduct: the popover stays open on
  // failure so the explanation is readable, and returns to the collection submenu on success so the
  // seller sees the name change where she clicked.
  async function pickCollection(id: string | null) {
    setBindingCollectionId(id ?? '__demo__');
    setPickCollectionError(null);
    try {
      await bindPreviewCollection(id);
      announce(
        id
          ? bt('chrome.coll.previewing_selected', 'Previewing with the selected collection.')
          : bt('chrome.coll.previewing_sample', 'Previewing with the sample collection.'),
      );
      close();
    } catch {
      setPickCollectionError(
        bt('chrome.coll.load_failed', 'Couldn’t load this collection. Is your store responding?'),
      );
    } finally {
      setBindingCollectionId(null);
    }
  }

  async function pickProduct(id: string | null) {
    setBindingId(id ?? '__demo__');
    setPickError(null);
    try {
      await bindPreviewProduct(id);
      announce(
        id
          ? bt('chrome.prod.previewing_selected', 'Previewing with the selected product.')
          : bt('chrome.prod.previewing_sample', 'Previewing with the sample product.'),
      );
      close();
    } catch {
      // Keep the popover OPEN on failure: closing it would hide the explanation and leave the canvas on
      // the previous product with no trace of what went wrong.
      setPickError(
        bt('chrome.prod.load_failed', 'Couldn’t load this product. Is your store responding?'),
      );
      announce(bt('chrome.prod.load_failed_sr', 'Could not load that product. Try again.'));
    } finally {
      setBindingId(null);
    }
  }

  // ── template ⇄ product reach (the facts the selector needs to stop lying by omission) ──
  // A product with NO suffix renders the base `default` page, so null and 'default' are the same page.
  const normalisedProductTemplate = previewProductTemplate ?? 'default';
  const handleLabel = (h: string) => (h === 'default' ? bt('chrome.tpl.default', 'Default') : h);
  const currentHandleLabel = handleLabel(currentHandle);
  const previewProductTemplateLabel = handleLabel(normalisedProductTemplate);
  /** Is the previewed product actually assigned to the layout on screen? */
  const assignedHere = normalisedProductTemplate === currentHandle;
  /**
   * May the create dialog join the new layout to a product? Only where an assignment means something: a
   * product route, outside a theme workspace (its bound product is an ephemeral demo with no catalog row),
   * with a REAL product on the canvas — and never in a read-only consultation.
   */
  const canAttachOnCreate =
    currentTemplate === 'product' && !inThemeWorkspace && previewProduct !== null && !readOnlyView;
  /**
   * Assignment is PUBLISHED-only server-side; mirror that here so the button never offers a certain 422.
   *
   * The list is not the only authority, and it is the slower one: while it is in flight `entries` still
   * holds the PREVIOUS page's layouts, so a template just created was absent from it and read as a draft —
   * the panel said "This template is a draft. Publish it before assigning it." and greyed the one button
   * that attaches the product, about a template the server had already published (walked 26/10/2026). The
   * open document knows its own answer: `publishedAt` is set the moment the create's publish lands.
   */
  const openEntry = entries.find((e) => e.handle === currentHandle);
  const currentTemplatePublished = openEntry
    ? openEntry.status === 'PUBLISHED'
    : publishedAt !== null;
  /**
   * Does the layout on screen reach NO product at all? Drives the pill on the closed switcher. Deliberately
   * narrow: a named product template (never `default`, which every unassigned product renders, and never a
   * theme workspace draft, which has no assignment), with counts actually loaded — `templateUsage === null`
   * is "don't know", which must stay silent rather than claim a zero.
   */
  const unusedHere =
    currentTemplate === 'product' &&
    !inThemeWorkspace &&
    currentHandle !== 'default' &&
    templateUsage !== null &&
    (templateUsage[currentHandle] ?? 0) === 0;
  /**
   * Is the product on screen NOT rendered by the layout on screen? The founder's morning (05/10/2026): Move
   * Shift on the canvas, `default` open underneath, "Product — Move Shift™ …" on the closed switcher, and the
   * one line that said "Uses move-shift" inside the popover, under a thumbnail. She switched the buy box to
   * the cart, published, and her store kept selling buy-now: the row it serves for that product is another
   * layout's.
   *
   * Known-and-different only: a pill that flashes on every cold boot would cry wolf. But "known" is a
   * question about the ANSWER, not about its value (26/10/2026) — this used to read `previewProductTemplate
   * !== null`, which made it silent for every product that renders `default`, i.e. every product the seller
   * has just created. That is precisely the case she hit: a new product on the canvas, a new layout open
   * underneath, and nothing on screen saying the two had never been joined.
   */
  const offProduct =
    currentTemplate === 'product' &&
    !inThemeWorkspace &&
    previewProduct !== null &&
    previewProductTemplateKnown &&
    normalisedProductTemplate !== currentHandle;

  /** Assign the previewed product to the open template, surfacing a failure instead of failing silently. */
  async function applyTemplateToProduct() {
    if (assigning) return;
    setAssigning(true);
    setAssignError(null);
    try {
      await assignTemplateToPreviewProduct(currentHandle === 'default' ? null : currentHandle);
      announce(
        btp('chrome.tpl.applied_sr', 'Template “{name}” applied to this product.', {
          name: currentHandleLabel,
        }),
      );
      setForm('none');
      pushToast(
        btp('chrome.tpl.applied_toast', '“{name}” now renders {product}.', {
          name: currentHandleLabel,
          product: previewProduct?.name ?? '',
        }),
        'success',
      );
    } catch {
      setAssignError(bt('chrome.tpl.assign_failed', 'Couldn’t apply this template. Try again.'));
    } finally {
      setAssigning(false);
    }
  }

  /** Publish the template on screen, then re-read the list so its badge (and the assign button) update. */
  async function publishOpenTemplate() {
    if (publishingTpl) return;
    setPublishingTpl(true);
    setPublishTplError(null);
    try {
      // No confirm dialog here, unlike the toolbar's Publish: this branch only shows for a template that
      // has never been published, so there is no live version to overwrite and nothing to warn about.
      const ok = await publish();
      if (!ok) throw new Error('publish refused');
      setTemplatesNonce((n) => n + 1);
      announce(
        btp('chrome.tpl.published_sr', 'Template “{name}” published.', {
          name: currentHandleLabel,
        }),
      );
    } catch {
      setPublishTplError(
        isReadOnlyView()
          ? bt(
              'chrome.view.readonly_write',
              'Read-only consultation — nothing can be changed on this store.',
            )
          : bt('chrome.tpl.publish_failed', 'Couldn’t publish. Try again.'),
      );
    } finally {
      setPublishingTpl(false);
    }
  }

  const drilledLabel =
    view === 'collection'
      ? (templateEntries().find((t) => t.template === 'collection')?.label ?? 'Collection')
      : (templateEntries().find((t) => t.template === 'product')?.label ?? 'Product');

  return (
    <div className="bld-pagesel" ref={rootRef}>
      <button
        ref={btnRef}
        type="button"
        className="bld-pagesel__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        title={
          currentTemplate === 'product' && previewProduct
            ? `${typeLabel}${suffix} — ${previewProduct.name}`
            : boundCollectionName
              ? `${typeLabel}${suffix} — ${boundCollectionName}`
              : `${typeLabel}${suffix}`
        }
        onClick={() => (open ? close() : openMenu())}
      >
        <span className="bld-pagesel__leadicon" aria-hidden="true">
          {templatePageIcon(currentTemplate, 15)}
        </span>
        <span className="bld-pagesel__name">
          {typeLabel}
          {suffix}
        </span>
        {/* REACH — the fact that was one popover away while the founder edited a layout nothing rendered
            (20/09/2026). Shown only for the case that misleads: a NAMED product template that no product
            uses. A loaded zero and a not-yet-loaded count must not look alike, hence the explicit `=== 0`
            on a non-null map; `default` is excluded because a product with no suffix renders it, so its
            count is never the whole story. */}
        {/* ONE pill, never two (26/10/2026). A layout created a moment ago is BOTH "no product uses it"
            and "not the one this product renders" — the two badges then sat side by side and pushed the
            product's name out of the button, 39px past its right edge. "Not on this product" is the
            sharper of the two (it names the product on screen), so it wins; the count is a line away in
            the panel. */}
        {unusedHere && !offProduct && (
          <span
            className="bld-pagesel__unused"
            title={bt(
              'chrome.tpl.unused_hint',
              'No product shows this layout. Apply it to a product to see it on your storefront.',
            )}
          >
            {bt('chrome.tpl.uses_none', 'No products')}
          </span>
        )}
        {/* THE OTHER HALF of that reach (05/10/2026): the layout reaches products, just not the one on the
            canvas. Same place, same shape, same rule — said on the closed switcher, before she edits. */}
        {offProduct && (
          <span
            className="bld-pagesel__unused bld-pagesel__offproduct"
            title={btp(
              'chrome.tpl.off_product_hint',
              '{product} shows the “{name}” layout, not this one. What you change here will not appear on it.',
              { product: previewProduct?.name ?? '', name: previewProductTemplateLabel },
            )}
          >
            {bt('chrome.tpl.off_product', 'Not on this product')}
          </span>
        )}
        {currentTemplate === 'product' && previewProduct && (
          <>
            <span className="bld-pagesel__boundsep" aria-hidden="true">
              —
            </span>
            <span className="bld-pagesel__bound">{previewProduct.name}</span>
          </>
        )}
        {/* The collection template names the collection it previews (22/09/2026): "Collection — MOVE
            Essentials" says out loud that this is the page of ONE collection, not the list of them. */}
        {boundCollectionName && (
          <>
            <span className="bld-pagesel__boundsep" aria-hidden="true">
              —
            </span>
            <span className="bld-pagesel__bound" data-testid="pagesel-bound-collection">
              {boundCollectionName}
            </span>
          </>
        )}
        <span className="dir-flip" aria-hidden="true">
          <ChevronDownIcon size={14} />
        </span>
      </button>
      {open && (
        <div
          className="bld-pagesel__panel"
          role="menu"
          aria-label={bt('chrome.pagesel.aria', 'Choose a page, template or preview product')}
        >
          {view === 'root' && (
            <>
              <div className="bld-pagesel__search">
                <span className="bld-pagesel__searchicon" aria-hidden="true">
                  <SearchIcon size={15} />
                </span>
                <input
                  type="text"
                  className="bld-pagesel__input"
                  placeholder={bt('chrome.pages.search', 'Search pages')}
                  aria-label={bt('chrome.pages.search', 'Search pages')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <ul className="bld-pagesel__list" role="none">
                {items.length === 0 ? (
                  <li className="bld-pagesel__empty">
                    {bt('chrome.pagesel.no_matches', 'No matches')}
                  </li>
                ) : (
                  items.map((t) => {
                    const seeded = Boolean(t.template);
                    const isCurrent = t.template === currentTemplate;
                    const drillable = !!t.template && DRILLABLE_TYPES.has(t.template);
                    return (
                      <li key={t.label} role="none">
                        <button
                          type="button"
                          role={drillable ? 'menuitem' : 'menuitemradio'}
                          aria-haspopup={drillable ? 'menu' : undefined}
                          aria-checked={drillable ? undefined : isCurrent}
                          aria-disabled={!seeded || undefined}
                          disabled={!seeded}
                          className="bld-pagesel__item"
                          title={
                            seeded ? undefined : bt('chrome.pagesel.coming_soon', 'Coming soon')
                          }
                          onClick={() => chooseType(t)}
                        >
                          <span className="bld-pagesel__itemlead" aria-hidden="true">
                            {seeded ? templatePageIcon(t.template, 15) : null}
                          </span>
                          <span className="bld-pagesel__itemlabel">{t.label}</span>
                          {!seeded && (
                            <span className="bld-pagesel__soon">
                              {bt('chrome.pagesel.soon', 'soon')}
                            </span>
                          )}
                          {isCurrent && seeded && (
                            <span className="bld-pagesel__nowdot" aria-hidden="true" />
                          )}
                          {drillable && (
                            <span className="bld-pagesel__drill dir-flip" aria-hidden="true">
                              <ChevronRightIcon size={14} />
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </>
          )}

          {view === 'page' && (
            <>
              <button type="button" className="bld-pagesel__back" onClick={() => setView('root')}>
                <span className="dir-flip" aria-hidden="true">
                  <ArrowLeftIcon size={15} />
                </span>
                Pages
              </button>

              <p className="bld-pagesel__grouphead">{bt('chrome.pages.yours', 'Your pages')}</p>
              <ul className="bld-pagesel__list" role="none">
                {pages.length === 0 ? (
                  <li className="bld-pagesel__empty">{bt('chrome.pages.none', 'No pages yet')}</li>
                ) : (
                  pages.map((p) => {
                    const isCurrent = currentTemplate === 'page' && p.handle === currentHandle;
                    if (renamingHandle === p.handle) {
                      return (
                        <li key={p.handle} role="none">
                          <form
                            className="bld-pagesel__tplform"
                            onSubmit={(e) => {
                              e.preventDefault();
                              void submitRename(p.handle);
                            }}
                          >
                            <input
                              type="text"
                              className="bld-pagesel__input"
                              aria-label={bt('chrome.pages.title_label', 'Page title')}
                              autoFocus
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                            />
                            <div className="bld-pagesel__tplactions">
                              <button
                                type="submit"
                                className="bld-pagesel__tplbtn bld-pagesel__tplbtn--primary"
                                disabled={renameValue.trim() === ''}
                              >
                                {bt('chrome.common.save', 'Save')}
                              </button>
                              <button
                                type="button"
                                className="bld-pagesel__tplbtn"
                                onClick={() => setRenamingHandle(null)}
                              >
                                {bt('chrome.common.cancel', 'Cancel')}
                              </button>
                            </div>
                          </form>
                        </li>
                      );
                    }
                    return (
                      <li key={p.handle} role="none" className="bld-pagesel__pagerow">
                        <button
                          type="button"
                          role="menuitemradio"
                          aria-checked={isCurrent}
                          className="bld-pagesel__item"
                          onClick={() => openPage(p.handle)}
                        >
                          <span className="bld-pagesel__itemlead" aria-hidden="true">
                            {templatePageIcon('page', 15)}
                          </span>
                          <span className="bld-pagesel__itemlabel">{p.title || p.handle}</span>
                          {isCurrent && <span className="bld-pagesel__nowdot" aria-hidden="true" />}
                        </button>
                        <button
                          type="button"
                          className="bld-pagesel__pageaction"
                          onClick={() => {
                            setRenamingHandle(p.handle);
                            setRenameValue(p.title ?? '');
                          }}
                        >
                          {bt('chrome.pages.rename', 'Rename')}
                        </button>
                        <button
                          type="button"
                          className="bld-pagesel__pageaction bld-pagesel__pageaction--danger"
                          aria-label={btp('chrome.pages.delete_aria', 'Delete {name}', {
                            name: p.title || p.handle,
                          })}
                          title={bt('chrome.pages.delete', 'Delete page')}
                          onClick={() => void removePage(p.handle, p.title || p.handle)}
                        >
                          <TrashIcon size={14} />
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>

              {pageFormOpen ? (
                <form
                  className="bld-pagesel__tplform"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void submitNewPage();
                  }}
                >
                  <label className="bld-pagesel__tpllabel" htmlFor="bld-new-page">
                    {bt('chrome.pages.new_title', 'New page title')}
                  </label>
                  <input
                    id="bld-new-page"
                    type="text"
                    className="bld-pagesel__input"
                    placeholder={bt('chrome.pages.title_ph', 'e.g. About us')}
                    aria-label={bt('chrome.pages.title_label', 'Page title')}
                    autoFocus
                    value={pageTitle}
                    onChange={(e) => setPageTitle(e.target.value)}
                  />

                  {/* "Start from" — a new page no longer has to begin blank: it can be built ON TOP of the
                      home page or of any page the seller already made (content copied, then independent). */}
                  <span className="bld-pagesel__tpllabel" id="bld-new-page-source">
                    {bt('chrome.pages.start_from', 'Start from')}
                  </span>
                  <ul
                    className="bld-pagesel__srclist"
                    role="radiogroup"
                    aria-labelledby="bld-new-page-source"
                  >
                    {pageSourceOptions.map((opt) => {
                      const selected = sourceKey(pageSource) === opt.key;
                      return (
                        <li key={opt.key} role="none">
                          <button
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            className={`bld-pagesel__src${selected ? ' is-selected' : ''}`}
                            onClick={() => setPageSource(opt.source)}
                          >
                            <span className="bld-pagesel__itemlead" aria-hidden="true">
                              {opt.icon}
                            </span>
                            <span className="bld-pagesel__itemlabel">{opt.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="bld-pagesel__tplactions">
                    <button
                      type="submit"
                      className="bld-pagesel__tplbtn bld-pagesel__tplbtn--primary"
                      disabled={pageTitle.trim() === '' || busy}
                    >
                      {busy
                        ? bt('chrome.common.creating', 'Creating…')
                        : bt('chrome.pages.create', 'Create page')}
                    </button>
                    <button
                      type="button"
                      className="bld-pagesel__tplbtn"
                      onClick={() => {
                        setPageFormOpen(false);
                        setPageTitle('');
                        setPageSource(null);
                      }}
                    >
                      {bt('chrome.common.cancel', 'Cancel')}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="bld-pagesel__tplactions">
                  <button
                    type="button"
                    className="bld-pagesel__tplbtn"
                    onClick={() => {
                      setPageFormOpen(true);
                      setPageTitle('');
                      setPageSource(null);
                    }}
                  >
                    <span className="bld-pagesel__plus" aria-hidden="true">
                      <PlusIcon size={13} />
                    </span>
                    {bt('chrome.pages.new', 'New page')}
                  </button>
                </div>
              )}
            </>
          )}

          {(view === 'product' || view === 'collection') && (
            <>
              <button type="button" className="bld-pagesel__back" onClick={() => setView('root')}>
                <span className="dir-flip" aria-hidden="true">
                  <ArrowLeftIcon size={15} />
                </span>
                {drilledLabel}
              </button>

              <p className="bld-pagesel__grouphead">
                {bt('chrome.tpl.layout', 'Layout')}
                {/* ONE spinner at a time: this one only for a REFRESH (a list is already on screen). When
                    there is nothing to show yet, the waiting row below carries the spinner instead. */}
                {entriesState === 'loading' && entries.length > 0 && (
                  <span className="bld-pagesel__spinner" aria-hidden="true" />
                )}
              </p>
              <ul className="bld-pagesel__list" role="none">
                {entries.map((e) => {
                  const h = e.handle;
                  const active = h === currentHandle || (inThemeWorkspace && h === 'default');
                  // Only product templates carry an assignment, so only they get a usage count. Not-loaded
                  // (`undefined`) must read differently from a real zero: the first shows nothing, the
                  // second warns that this layout reaches no product at all.
                  const count =
                    view === 'product' && templateUsage ? (templateUsage[h] ?? 0) : undefined;
                  // A DRAFT template cannot be assigned at all, so "Aucun produit" next to "Brouillon"
                  // states the same fact twice — and the two together crowd the name into an ellipsis.
                  // The badge already carries the meaning; drop the redundant zero (never a real count).
                  const uses = e.status === 'DRAFT' && count === 0 ? undefined : count;
                  return (
                    <li key={h} role="none">
                      <button
                        type="button"
                        role="menuitemradio"
                        // In a theme workspace the lone 'default' row IS the open page (see the handle
                        // discovery effect), so it reads as active even though `currentHandle` is the
                        // internal `theme-ws-*` value.
                        aria-checked={active}
                        className="bld-pagesel__item"
                        onClick={() => chooseHandle(h)}
                      >
                        <span className="bld-pagesel__itemlead" aria-hidden="true">
                          {templatePageIcon(view, 15)}
                        </span>
                        <span className="bld-pagesel__tplmain">
                          <span className="bld-pagesel__itemlabel">
                            {h === 'default' ? bt('chrome.tpl.default', 'Default') : h}
                          </span>
                          {uses !== undefined && (
                            <span className="bld-pagesel__tpluse">
                              {uses === 0
                                ? bt('chrome.tpl.uses_none', 'No products')
                                : uses === 1
                                  ? bt('chrome.tpl.uses_one', '1 product')
                                  : btp('chrome.tpl.uses_many', '{count} products', {
                                      count: uses,
                                    })}
                            </span>
                          )}
                        </span>
                        {e.status === 'DRAFT' && (
                          <span
                            className="bld-pagesel__tplbadge"
                            title={bt('chrome.tpl.draft_hint', 'Publish it before assigning it')}
                          >
                            {bt('chrome.tpl.draft', 'Draft')}
                          </span>
                        )}
                        {active && <span className="bld-pagesel__nowdot" aria-hidden="true" />}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {entriesState === 'loading' && entries.length === 0 && (
                <p className="bld-pagesel__empty bld-pagesel__loading" role="status">
                  <span className="bld-pagesel__spinner" aria-hidden="true" />
                  {bt('chrome.tpl.loading', 'Loading your layouts…')}
                </p>
              )}
              {entriesState === 'error' && (
                <p className="bld-pagesel__empty">
                  {bt('chrome.tpl.list_failed', 'Incomplete list — the store didn’t respond.')}{' '}
                  <button
                    type="button"
                    className="bld-pagesel__retry"
                    onClick={() => setTemplatesNonce((n) => n + 1)}
                  >
                    {bt('chrome.common.retry', 'Retry')}
                  </button>
                </p>
              )}

              {/* Named templates are a LIVE-theme concept: both actions mint a live handle, and the
                  duplicate label would print the reserved `theme-ws-*` handle back at the seller. Inside a
                  theme workspace there is exactly one layout — the theme's default — so neither is offered. */}
              {inThemeWorkspace ? null : (
                <div className="bld-pagesel__tplactions">
                  <button
                    type="button"
                    className="bld-pagesel__tplbtn"
                    disabled={readOnlyView}
                    title={
                      readOnlyView
                        ? bt('chrome.view.readonly', 'Read-only consultation')
                        : undefined
                    }
                    onClick={() => {
                      // The dialog opens on the template she is LOOKING AT as the one to copy — the
                      // commonest intent by far ("another one like this"), and still one dropdown away
                      // from any other. The popover closes under it: the dialog is portalled to the body,
                      // so leaving the menu open would only put a panel behind a modal.
                      setSource(currentHandle);
                      setName('');
                      setCreateError(null);
                      // Re-armed on every open: a seller who unticked it once for a shared layout must not
                      // find her next product-sized layout silently detached too.
                      setAttachToProduct(true);
                      setForm('create');
                      close();
                    }}
                  >
                    {bt('chrome.tpl.create_cta', 'Create template')}
                  </button>
                </div>
              )}

              {/* Preview block — product templates only. Collection preview isn't built yet, so its
                  submenu is Layout-only (0021 / deferred collection-preview feature). */}
              {view === 'product' && (
                <div className="bld-pagesel__preview">
                  <p className="bld-pagesel__grouphead">
                    {bt('chrome.prod.previewing', 'Previewing')}
                  </p>
                  {/* No "preview only" caption here any more: it cost two lines to say what the assignment
                      block below now states outright (which template the product uses, and how to change
                      it) — and it read as a lie sitting above a button that DOES change the product. */}
                  <button
                    type="button"
                    className="bld-pagesel__previewrow"
                    onClick={() => {
                      setProductQuery('');
                      setPickError(null);
                      setView('products');
                    }}
                  >
                    <ProductThumb product={previewProduct} />
                    <span className="bld-pagesel__previewname">
                      {previewProduct
                        ? previewProduct.name
                        : bt('chrome.prod.sample', 'Sample product')}
                    </span>
                    <span className="bld-pagesel__change">
                      {bt('chrome.prod.change', 'Change')}
                    </span>
                  </button>

                  {/* The loop-closer: previewing a product does NOT assign it, so state the product's REAL
                      template and offer the one-click assignment. Without this the seller has to leave the
                      builder for the dashboard product form to make the layout they just built take effect. */}
                  {/* Not inside a theme workspace: there the bound product is an EPHEMERAL demo with no
                      catalog row (assigning it would 404), and the workspace draft is the theme's own
                      default page, not an assignable named template — same rule as create/duplicate above. */}
                  {previewProduct && !inThemeWorkspace && (
                    <div className="bld-pagesel__assign">
                      {/* One short line. The "…and not the one you have open" half was dropped: the very
                          next control offers to apply the open template, which says it without the words. */}
                      <p className="bld-pagesel__assignstate">
                        {bt('chrome.tpl.uses_label', 'Uses')}{' '}
                        <strong>
                          {assignedHere ? currentHandleLabel : previewProductTemplateLabel}
                        </strong>
                      </p>
                      {!assignedHere && (
                        <button
                          type="button"
                          className="bld-pagesel__tplbtn bld-pagesel__tplbtn--primary"
                          disabled={assigning || !currentTemplatePublished || readOnlyView}
                          title={
                            readOnlyView
                              ? bt('chrome.view.readonly', 'Read-only consultation')
                              : currentTemplatePublished
                                ? undefined
                                : bt(
                                    'chrome.tpl.publish_before_assign',
                                    'Publish this template before assigning it',
                                  )
                          }
                          onClick={() => {
                            // Confirm first, and name both sides there. This button used to write
                            // straight through: one click silently repointed a live product page at a
                            // different layout, with only a live-region announcement to show for it.
                            setAssignError(null);
                            setForm('assign');
                            close();
                          }}
                        >
                          {/* "à ce produit" is dropped — the block sits under the product, so the context
                              is already on screen and the label fits on one line at panel width. */}
                          {assigning
                            ? bt('chrome.tpl.applying', 'Applying…')
                            : btp('chrome.tpl.apply', 'Apply “{name}”', {
                                name: currentHandleLabel,
                              })}
                        </button>
                      )}
                      {!currentTemplatePublished && !assignedHere && (
                        <>
                          <p className="bld-pagesel__assignhint">
                            {bt(
                              'chrome.tpl.draft_explain',
                              'This template is a draft. Publish it before assigning it.',
                            )}
                          </p>
                          <button
                            type="button"
                            className="bld-pagesel__tplbtn"
                            disabled={publishingTpl || readOnlyView}
                            title={
                              readOnlyView
                                ? bt('chrome.view.readonly', 'Read-only consultation')
                                : undefined
                            }
                            onClick={() => void publishOpenTemplate()}
                          >
                            {publishingTpl
                              ? bt('chrome.common.publishing', 'Publishing…')
                              : bt('chrome.tpl.publish', 'Publish this template')}
                          </button>
                          {publishTplError && (
                            <p className="bld-pagesel__tplerror" role="alert">
                              {publishTplError}
                            </p>
                          )}
                        </>
                      )}
                      {assignError && <p className="bld-pagesel__tplerror">{assignError}</p>}
                    </div>
                  )}
                </div>
              )}
              {/* Preview block — collection templates (22/09/2026). The template is the page of ONE
                  collection: say which one the canvas shows and let the seller change it. Until this block
                  existed the canvas painted the demo collection and there was no way to see one's own. */}
              {view === 'collection' && (
                <div className="bld-pagesel__preview">
                  <p className="bld-pagesel__grouphead">
                    {bt('chrome.prod.previewing', 'Previewing')}
                  </p>
                  <button
                    type="button"
                    className="bld-pagesel__previewrow"
                    data-testid="pagesel-preview-collection"
                    onClick={() => {
                      setCollectionQuery('');
                      setPickCollectionError(null);
                      setView('collections');
                    }}
                  >
                    <CollectionThumb collection={previewCollection} />
                    <span className="bld-pagesel__previewname">
                      {previewCollection
                        ? previewCollection.name
                        : bt('chrome.coll.sample', 'Sample collection')}
                    </span>
                    <span className="bld-pagesel__change">
                      {bt('chrome.prod.change', 'Change')}
                    </span>
                  </button>
                </div>
              )}
            </>
          )}

          {view === 'collections' && (
            <>
              <button
                type="button"
                className="bld-pagesel__back"
                onClick={() => setView('collection')}
              >
                <span className="dir-flip" aria-hidden="true">
                  <ArrowLeftIcon size={15} />
                </span>
                {bt('chrome.coll.preview_back', 'Which collection to show')}
              </button>
              <div className="bld-pagesel__search">
                <span className="bld-pagesel__searchicon" aria-hidden="true">
                  <SearchIcon size={15} />
                </span>
                <input
                  type="text"
                  className="bld-pagesel__input"
                  placeholder={bt('chrome.coll.search', 'Search collections')}
                  aria-label={bt('chrome.coll.search', 'Search collections')}
                  value={collectionQuery}
                  onChange={(e) => setCollectionQuery(e.target.value)}
                />
              </div>
              <ul className="bld-pagesel__list" role="none">
                {/* The SAMPLE collection is a stand-in for a shop that has no collection yet — nothing more.
                    Next to a real one it reads as a second, mysterious collection (« un S, donc on affiche
                    tout ? », 22/09/2026), so it is offered ONLY while the store has none to show. */}
                {collectionsCatalogState === 'loaded' && collectionsCatalog.length === 0 && (
                  <li role="none">
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={previewCollection === null}
                      disabled={bindingCollectionId !== null}
                      className="bld-pagesel__item bld-pagesel__item--product"
                      onClick={() => void pickCollection(null)}
                    >
                      <CollectionThumb collection={null} />
                      <span className="bld-pagesel__itemlabel">
                        {bt('chrome.coll.sample', 'Sample collection')}
                      </span>
                      {bindingCollectionId === '__demo__' && (
                        <span className="bld-pagesel__soon">
                          {bt('chrome.common.loading', 'loading…')}
                        </span>
                      )}
                    </button>
                  </li>
                )}
                {collectionsCatalogState === 'loading' && collectionsCatalog.length === 0 && (
                  <li className="bld-pagesel__empty">
                    {bt('chrome.coll.loading', 'Loading your collections…')}
                  </li>
                )}
                {collectionsCatalogState === 'error' && (
                  <li className="bld-pagesel__empty">
                    {bt('chrome.coll.list_failed', 'Couldn’t load your collections.')}{' '}
                    <button
                      type="button"
                      className="bld-pagesel__retry"
                      onClick={() => void loadCollectionsCatalog()}
                    >
                      {bt('chrome.common.retry', 'Retry')}
                    </button>
                  </li>
                )}
                {collectionsCatalogState === 'loaded' &&
                  filteredCollections.length === 0 &&
                  collectionQuery.trim() !== '' && (
                    <li className="bld-pagesel__empty">
                      {bt('chrome.pagesel.no_matches', 'No matches')}
                    </li>
                  )}
                {collectionsCatalogState === 'loaded' && collectionsCatalog.length === 0 && (
                  <li className="bld-pagesel__empty">
                    {bt('chrome.coll.none', 'No collections yet')}
                  </li>
                )}
                {filteredCollections.map((c) => (
                  <li key={c.id} role="none">
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={previewCollection?.id === c.id}
                      disabled={bindingCollectionId !== null}
                      className="bld-pagesel__item bld-pagesel__item--product"
                      onClick={() => void pickCollection(c.id)}
                    >
                      <CollectionThumb collection={c} />
                      <span className="bld-pagesel__itemlabel">{c.name}</span>
                      {previewCollection?.id === c.id && (
                        <span className="bld-pagesel__nowdot" aria-hidden="true" />
                      )}
                      {bindingCollectionId === c.id && (
                        <span className="bld-pagesel__soon">
                          {bt('chrome.common.loading', 'loading…')}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {pickCollectionError && (
                <p className="bld-pagesel__tplerror" role="alert">
                  {pickCollectionError}
                </p>
              )}
            </>
          )}

          {view === 'products' && (
            <>
              <button
                type="button"
                className="bld-pagesel__back"
                onClick={() => setView('product')}
              >
                <span className="dir-flip" aria-hidden="true">
                  <ArrowLeftIcon size={15} />
                </span>
                Preview product
              </button>
              <div className="bld-pagesel__search">
                <span className="bld-pagesel__searchicon" aria-hidden="true">
                  <SearchIcon size={15} />
                </span>
                <input
                  type="text"
                  className="bld-pagesel__input"
                  placeholder={bt('chrome.prod.search', 'Search products')}
                  aria-label={bt('chrome.prod.search', 'Search products')}
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                />
              </div>
              <ul className="bld-pagesel__list" role="none">
                <li role="none">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={previewProduct === null}
                    disabled={bindingId !== null}
                    className="bld-pagesel__item bld-pagesel__item--product"
                    onClick={() => void pickProduct(null)}
                  >
                    <ProductThumb product={null} />
                    <span className="bld-pagesel__itemlabel">
                      {bt('chrome.prod.sample', 'Sample product')}
                    </span>
                    {bindingId === '__demo__' && (
                      <span className="bld-pagesel__soon">
                        {bt('chrome.common.loading', 'loading…')}
                      </span>
                    )}
                  </button>
                </li>
                {catalogState === 'loading' && catalog.length === 0 && (
                  <li className="bld-pagesel__empty">
                    {bt('chrome.prod.loading', 'Loading your products…')}
                  </li>
                )}
                {catalogState === 'error' && (
                  <li className="bld-pagesel__empty">
                    Couldn’t load your products.{' '}
                    <button
                      type="button"
                      className="bld-pagesel__retry"
                      onClick={() => void loadCatalog()}
                    >
                      Retry
                    </button>
                  </li>
                )}
                {catalogState === 'loaded' && filteredProducts.length === 0 && (
                  <li className="bld-pagesel__empty">
                    {productQuery.trim()
                      ? bt('chrome.pagesel.no_matches', 'No matches')
                      : bt('chrome.prod.none', 'No products yet')}
                  </li>
                )}
                {filteredProducts.map((p) => (
                  <li key={p.id} role="none">
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={previewProduct?.id === p.id}
                      disabled={bindingId !== null}
                      className="bld-pagesel__item bld-pagesel__item--product"
                      onClick={() => void pickProduct(p.id)}
                    >
                      <ProductThumb product={p} />
                      <span className="bld-pagesel__itemlabel">{p.name}</span>
                      {previewProduct?.id === p.id && (
                        <span className="bld-pagesel__nowdot" aria-hidden="true" />
                      )}
                      {bindingId === p.id && (
                        <span className="bld-pagesel__soon">
                          {bt('chrome.common.loading', 'loading…')}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {pickError && (
                <p className="bld-pagesel__tplerror" role="alert">
                  {pickError}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* CREATE A TEMPLATE — one door, two fields (founder, 19/09/2026): the name, and the template it
          starts from. There is no blank start any more: a template born from the theme's raw seed looked
          like no page the seller had ever seen, so every new one is a copy of one she can point at. */}
      {form === 'create' && (
        <Modal
          titleId="bld-new-tpl-title"
          dismissible={!busy}
          onClose={() => {
            setForm('none');
            setName('');
            setCreateError(null);
          }}
        >
          <h2 id="bld-new-tpl-title" className="bld-modal__title">
            {btp('chrome.tpl.create_title', 'New template · {type}', { type: typeLabel })}
          </h2>
          <form
            className="bld-tpldlg"
            onSubmit={(e) => {
              e.preventDefault();
              void submitForm();
            }}
          >
            <label className="bld-tpldlg__label" htmlFor="bld-new-tpl">
              {bt('chrome.tpl.name_label', 'Template name')}
            </label>
            <input
              id="bld-new-tpl"
              type="text"
              className="bld-tpldlg__input"
              placeholder={bt('chrome.tpl.name_ph', 'e.g. advertorial')}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {name.trim() !== '' && nameError && <p className="bld-tpldlg__error">{nameError}</p>}
            {name.trim() !== '' && !nameError && toHandle(name) !== name.trim() && (
              <p className="bld-tpldlg__hint">
                {btp('chrome.tpl.name_hint', 'Will be saved as “{handle}”', {
                  handle: toHandle(name),
                })}
              </p>
            )}

            <span className="bld-tpldlg__label" id="bld-new-tpl-src">
              {bt('chrome.tpl.based_on', 'Based on')}
            </span>
            {/* A compact radio list, NEVER a native <select> (build-plan §7.9 — no native popups; the
                pages "Start from" picker above follows the same rule). It carries the Layout group's own
                labels, and is never empty: `default` is always listed, and `entries` is already loaded
                because the dialog is only reachable from the open menu. */}
            <ul
              className="bld-pagesel__srclist bld-tpldlg__srclist"
              role="radiogroup"
              aria-labelledby="bld-new-tpl-src"
            >
              {entries.map((e) => (
                <li key={e.handle} role="none">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={source === e.handle}
                    className={`bld-pagesel__src${source === e.handle ? ' is-selected' : ''}`}
                    onClick={() => setSource(e.handle)}
                  >
                    <span className="bld-pagesel__itemlead" aria-hidden="true">
                      {templatePageIcon(currentTemplate, 15)}
                    </span>
                    <span className="bld-pagesel__itemlabel">{handleLabel(e.handle)}</span>
                    {/* The `is-selected` tint alone is #f1f1f1 on white — all but invisible, and this row
                        decides WHAT the new template copies. The dot is the Layout list's own "you are
                        here" mark, so the two lists say "this one" the same way. */}
                    {source === e.handle && (
                      <span className="bld-pagesel__nowdot" aria-hidden="true" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
            <p className="bld-tpldlg__hint">
              {bt(
                'chrome.tpl.based_on_hint',
                'The new template opens as a copy of this one — editing it never changes the original.',
              )}
            </p>
            {/* THE PRODUCT (26/10/2026). A layout created while a product is on the canvas now goes ON
                that product, and the dialog says so in the seller's own terms — with the product named, so
                the write is never implicit. Unticking it is the "a layout to assign later" intent, and the
                hint below then states what the product keeps rendering instead of leaving her to find out
                from a storefront that did not change. */}
            {canAttachOnCreate && previewProduct && (
              <>
                <label className="bld-tpldlg__check">
                  <input
                    type="checkbox"
                    checked={attachToProduct}
                    onChange={(e) => setAttachToProduct(e.target.checked)}
                  />
                  <span>
                    {btp('chrome.tpl.create_attach', 'Apply it to {product}', {
                      product: previewProduct.name,
                    })}
                  </span>
                </label>
                <p
                  className={`bld-tpldlg__hint${attachToProduct ? '' : ' bld-tpldlg__hint--warn'}`}
                >
                  {attachToProduct
                    ? btp(
                        'chrome.tpl.create_attach_hint',
                        'Visitors opening {product} will see this new layout as soon as it is created.',
                        { product: previewProduct.name },
                      )
                    : btp(
                        'chrome.tpl.create_detach_hint',
                        'No product will show it. {product} keeps showing “{previous}”, and you can apply the new layout later from this menu.',
                        {
                          product: previewProduct.name,
                          previous: previewProductTemplateLabel,
                        },
                      )}
                </p>
              </>
            )}
            {/* The sentence that would have saved the founder an afternoon (20/09/2026): she created a
                template, edited it, hit Publish, and went looking for the change on her storefront.
                Creating a layout reaches NOTHING on its own. Shown when there is no real product to offer
                it to — with one on the canvas, the checkbox above says the same thing and acts on it.
                Product templates only: pages and the other types carry no product assignment. */}
            {view === 'product' && !canAttachOnCreate && (
              <p className="bld-tpldlg__hint bld-tpldlg__hint--warn">
                {bt(
                  'chrome.tpl.born_unused',
                  'No product will show it. To see it on your storefront, apply it to a product.',
                )}
              </p>
            )}

            {createError && (
              <p className="bld-tpldlg__error" role="alert">
                {createError}
              </p>
            )}
            <div className="bld-modal__actions">
              <button
                type="button"
                className="bld-btn bld-btn--ghost"
                disabled={busy}
                onClick={() => {
                  setForm('none');
                  setName('');
                  setCreateError(null);
                }}
              >
                {bt('chrome.common.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                className="bld-btn bld-btn--primary"
                disabled={!!nameError || name.trim() === '' || busy}
              >
                {busy
                  ? bt('chrome.common.creating', 'Creating…')
                  : bt('chrome.common.create', 'Create')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* APPLY — the confirm in front of the only write that changes what a visitor sees on a product
          page. It names the template AND the product (founder, 20/09/2026), because "Apply “X”" sitting
          under a thumbnail left the target implicit, and the previewed product is not always the one she
          has in mind. Portalled to the body like the create dialog, so the popover closes underneath. */}
      {form === 'assign' && previewProduct && (
        <Modal
          titleId="bld-assign-tpl-title"
          dismissible={!assigning}
          onClose={() => {
            setForm('none');
            setAssignError(null);
          }}
        >
          <h2 id="bld-assign-tpl-title" className="bld-modal__title">
            {btp('chrome.tpl.assign_q', 'Apply “{name}” to {product}?', {
              name: currentHandleLabel,
              product: previewProduct.name,
            })}
          </h2>
          <p className="bld-modal__body">
            {btp(
              'chrome.tpl.assign_body',
              'Visitors opening {product} will see this layout. It leaves “{previous}”.',
              { product: previewProduct.name, previous: previewProductTemplateLabel },
            )}
          </p>
          {assignError && (
            <p className="bld-tpldlg__error" role="alert">
              {assignError}
            </p>
          )}
          <div className="bld-modal__actions">
            <button
              type="button"
              className="bld-btn bld-btn--ghost"
              disabled={assigning}
              onClick={() => {
                setForm('none');
                setAssignError(null);
              }}
            >
              {bt('chrome.common.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              className="bld-btn bld-btn--primary"
              disabled={assigning}
              onClick={() => void applyTemplateToProduct()}
            >
              {assigning
                ? bt('chrome.tpl.applying', 'Applying…')
                : bt('chrome.tpl.assign_cta', 'Apply')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * Small product cover thumbnail for the preview-product rows (0021). Shows the cover image when present,
 * else a neutral initial tile — never a broken <img>. Accepts either a lean catalog row (coverImageUrl) or
 * a bound StorefrontProduct (its cover image), or null for the built-in sample product.
 */
/** The collection counterpart of {@link ProductThumb}: its cover image, else its initial. */
function CollectionThumb({
  collection,
}: {
  collection: { name: string; coverImage?: { url: string } | null } | null;
}) {
  const url = collection?.coverImage?.url ?? null;
  if (url) {
    return <img className="bld-pagesel__thumb" src={url} alt="" aria-hidden="true" />;
  }
  const initial = (collection?.name ?? 'S').trim().charAt(0).toUpperCase() || 'S';
  return (
    <span className="bld-pagesel__thumb bld-pagesel__thumb--placeholder" aria-hidden="true">
      {initial}
    </span>
  );
}

function ProductThumb({
  product,
}: {
  product: {
    name: string;
    coverImageUrl?: string | null;
    images?: ReadonlyArray<{ url: string; isCover: boolean }>;
  } | null;
}) {
  const url = product?.coverImageUrl ?? product?.images?.[0]?.url ?? null;
  if (url) {
    return <img className="bld-pagesel__thumb" src={url} alt="" aria-hidden="true" />;
  }
  const initial = (product?.name ?? 'S').trim().charAt(0).toUpperCase() || 'S';
  return (
    <span className="bld-pagesel__thumb bld-pagesel__thumb--placeholder" aria-hidden="true">
      {initial}
    </span>
  );
}

/** RIGHT "..." more menu — the "Preview right-to-left" toggle lives here. It flips ONLY the store PREVIEW
 *  (canvas content), never the builder chrome, which is always LTR (founder feedback 2026-07-04). */
function MoreMenu() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const previewDir = useBuilderStore((s) => s.renderContext?.dir ?? 'ltr');
  const togglePreviewDir = useBuilderStore((s) => s.togglePreviewDir);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="bld-rowmenu" ref={rootRef}>
      <button
        ref={btnRef}
        type="button"
        className="bld-iconbtn-top"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={bt('chrome.top.more', 'More options')}
        title={bt('chrome.top.more', 'More options')}
        onClick={() => setOpen((v) => !v)}
      >
        <MoreIcon />
      </button>
      {open && (
        <div
          className="bld-rowmenu__panel"
          role="menu"
          aria-label={bt('chrome.top.more', 'More options')}
        >
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={previewDir === 'rtl'}
            className="bld-rowmenu__item"
            aria-label={
              previewDir === 'rtl'
                ? bt(
                    'chrome.top.dir_aria_rtl',
                    'Store preview direction: right-to-left. Switch the preview to left-to-right.',
                  )
                : bt(
                    'chrome.top.dir_aria_ltr',
                    'Store preview direction: left-to-right. Switch the preview to right-to-left.',
                  )
            }
            onClick={() => {
              togglePreviewDir();
              setOpen(false);
            }}
          >
            {previewDir === 'rtl'
              ? bt('chrome.top.preview_ltr', 'Preview left-to-right')
              : bt('chrome.top.preview_rtl', 'Preview right-to-left')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * MOBILE "…" more-menu (Shopify mobile parity) — the condensed topbar moves the icon-rail tabs + undo /
 * redo + the inspect toggle into this menu, mirroring Shopify's mobile menu ("Activate inspector / Undo /
 * Redo / Sections / Theme settings"). Picking a tab sets the chromeStore tab AND opens the bottom sheet
 * (un-collapses + deselects the section so the tab's panel shows); the inspect toggle flips preview mode.
 * All wired to EXISTING state only.
 */
function MobileMoreMenu() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const undo = useBuilderStore((s) => s.undo);
  const redo = useBuilderStore((s) => s.redo);
  const canUndo = useBuilderStore((s) => s.past.length > 0);
  const canRedo = useBuilderStore((s) => s.future.length > 0);
  const previewMode = useBuilderStore((s) => s.previewMode);
  const togglePreviewMode = useBuilderStore((s) => s.togglePreviewMode);
  const selectSection = useBuilderStore((s) => s.selectSection);
  const storeSlug = useBuilderStore((s) => s.storeSlug);
  const storePrimaryDomain = useBuilderStore((s) => s.storePrimaryDomain);

  const sidebarTab = useChromeStore((s) => s.sidebarTab);
  const selectSidebarTab = useChromeStore((s) => s.selectSidebarTab);
  const setSidebarCollapsed = useChromeStore((s) => s.setSidebarCollapsed);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function openPanel(tab: SidebarTab) {
    setOpen(false);
    // Always SHOW the tab's panel in the sheet: switch tab, clear any selection (so the tab content,
    // not the inspector, renders), and force the sheet open even if the same tab was active.
    if (tab !== sidebarTab) selectSidebarTab(tab);
    selectSection(null);
    setSidebarCollapsed(false);
  }

  return (
    <div className="bld-rowmenu" ref={rootRef}>
      <button
        ref={btnRef}
        type="button"
        className="bld-iconbtn-top"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={bt('chrome.top.more', 'More options')}
        title={bt('chrome.top.more', 'More options')}
        onClick={() => setOpen((v) => !v)}
      >
        <MoreIcon />
      </button>
      {open && (
        <div
          className="bld-rowmenu__panel bld-rowmenu__panel--wide"
          role="menu"
          aria-label={bt('chrome.top.more', 'More options')}
        >
          <button
            type="button"
            role="menuitem"
            className="bld-rowmenu__item"
            disabled={!storeSlug}
            onClick={() => {
              openStorefront(storeSlug, storePrimaryDomain);
              setOpen(false);
            }}
          >
            <span className="bld-rowmenu__item-icon" aria-hidden="true">
              <ViewStoreIcon size={16} />
            </span>
            {bt('chrome.top.view_store', 'View store')}
          </button>
          <div className="bld-rowmenu__sep" role="separator" />
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={previewMode}
            className="bld-rowmenu__item"
            onClick={() => {
              togglePreviewMode();
              setOpen(false);
              announce(
                useBuilderStore.getState().previewMode
                  ? bt(
                      'chrome.top.preview_on_sr',
                      'Preview mode on. Showing your page as visitors see it.',
                    )
                  : bt('chrome.top.preview_off_sr', 'Preview mode off. Back to editing.'),
              );
            }}
          >
            <span className="bld-rowmenu__item-icon" aria-hidden="true">
              <InspectorIcon size={16} />
            </span>
            {previewMode
              ? bt('chrome.top.exit_preview', 'Exit preview')
              : bt('chrome.top.activate_inspector', 'Activate inspector')}
          </button>
          <button
            type="button"
            role="menuitem"
            className="bld-rowmenu__item"
            disabled={!canUndo}
            onClick={() => {
              undo();
              setOpen(false);
            }}
          >
            <span className="bld-rowmenu__item-icon dir-flip" aria-hidden="true">
              <UndoIcon size={16} />
            </span>
            {bt('chrome.top.undo', 'Undo')}
          </button>
          <button
            type="button"
            role="menuitem"
            className="bld-rowmenu__item"
            disabled={!canRedo}
            onClick={() => {
              redo();
              setOpen(false);
            }}
          >
            <span className="bld-rowmenu__item-icon dir-flip" aria-hidden="true">
              <RedoIcon size={16} />
            </span>
            {bt('chrome.top.redo', 'Redo')}
          </button>
          <div className="bld-rowmenu__sep" role="separator" />
          {TABS.map(({ value, label, icon }) => (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={sidebarTab === value}
              className="bld-rowmenu__item"
              onClick={() => openPanel(value)}
            >
              <span className="bld-rowmenu__item-icon" aria-hidden="true">
                {icon}
              </span>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Open the seller's LIVE public storefront (what customers see) in a NEW tab. `noopener,noreferrer`
 * severs the `window.opener` link (the storefront can't reach back into the builder) — no data leak.
 * Best-effort: does nothing until the slug has loaded. `primaryDomain` (0046) is the shop's custom address
 * when it has one: on a deployed builder that is where the shop lives, and where the dashboard's eye goes.
 */
function openStorefront(slug: string | null, primaryDomain: string | null): void {
  if (!slug) return;
  window.open(deriveStorefrontUrl(slug, primaryDomain), '_blank', 'noopener,noreferrer');
  announce(bt('chrome.top.opening_store_sr', 'Opening your storefront in a new tab.'));
}

/**
 * Open the storefront rendering the DRAFT pages of [handle] (feature 0034). Mints a short-lived grant
 * server-side first, so nothing here decides what may be previewed — the backend checks the handle's shape
 * AND that it belongs to this seller before it signs anything.
 *
 * Opened via `window.open(url)` AFTER the await rather than a pre-opened blank tab: a mint takes one round
 * trip, and a blank tab that then fails to fill is worse than a moment's wait. Popup blockers generally
 * allow it because the call still originates from the click's task.
 */
async function openPreview(
  slug: string,
  handle: string,
  /** Undefined = the store's own language (feature 0050 lot 0). */
  locale: string | undefined,
  themeName: string | null,
  primaryDomain: string | null,
): Promise<void> {
  const minted = await builderApi.mintPreviewToken(handle, locale);
  window.open(
    derivePreviewUrl(slug, minted.token, themeName, primaryDomain),
    '_blank',
    'noopener,noreferrer',
  );
}

/**
 * "Preview" — a ghost button that opens the seller's storefront in a new tab. What it shows depends on where
 * the seller is standing, which is the whole point of feature 0034:
 *
 *  - on a LIVE route → the published store, exactly as before;
 *  - inside a THEME WORKSPACE → that theme's DRAFT pages, on the real storefront, behind a short-lived
 *    grant. Before 0034 this button opened the published store from a theme workspace too, so a seller
 *    customising Atlas while Volt was live clicked "Preview" and got Volt — the bug that started all this.
 *
 * The builder only knows the opaque store_id from the JWT, so `Topbar` best-effort loads the store SLUG
 * (`loadStore`) and the URL is built from it. Disabled until the slug is known (or if it failed to load).
 */
function PreviewStoreButton() {
  const storeSlug = useBuilderStore((s) => s.storeSlug);
  const storeState = useBuilderStore((s) => s.storeState);
  const route = useBuilderStore((s) => s.route);
  const loadStore = useBuilderStore((s) => s.loadStore);
  const [minting, setMinting] = useState(false);

  const inWorkspace = isThemeWorkspaceHandle(route.handle);
  const themeName = inWorkspace
    ? (getThemeListing(themeIdOfWorkspaceHandle(route.handle))?.name ??
      bt('chrome.top.this_theme', 'This theme'))
    : null;
  // The slug is fetched ONCE at boot, fire-and-forget. A transient failure there (backend restart, DB
  // connections dropped after a laptop sleep) used to grey this button for the WHOLE session behind a
  // tooltip that lied — "Loading your store…" while nothing was loading and nothing would retry. So an
  // errored load stays CLICKABLE: the click re-runs `loadStore()` (idempotent, and it only short-circuits
  // on 'loading'/'loaded') and then opens the preview.
  const failed = storeState === 'error';
  const ready = (failed || (storeState === 'loaded' && !!storeSlug)) && !minting;

  async function onClick(): Promise<void> {
    setMinting(true);
    try {
      let slug = storeSlug;
      if (!slug) {
        await loadStore();
        slug = useBuilderStore.getState().storeSlug;
      }
      if (!slug) {
        pushToast(
          bt('chrome.top.store_load_failed', 'Couldn’t load your store. Please reload the page.'),
          'error',
        );
        return;
      }
      // Read AFTER the lazy load above, never from a hook captured before it: a retried identity brings
      // the shop's address along with its slug, and the old address is exactly the bug.
      const primaryDomain = useBuilderStore.getState().storePrimaryDomain;
      if (!inWorkspace) {
        openStorefront(slug, primaryDomain);
        return;
      }
      await openPreview(slug, route.handle, route.locale, themeName, primaryDomain);
      announce(
        btp('chrome.top.opening_preview_sr', 'Opening a preview of {name} in a new tab.', {
          name: themeName ?? '',
        }),
      );
    } catch {
      // A failed mint must never look like a successful preview — the seller would go looking for changes
      // on a live store that never changed.
      pushToast(
        bt('chrome.top.preview_failed', 'Couldn’t open the preview. Please try again.'),
        'error',
      );
    } finally {
      setMinting(false);
    }
  }

  return (
    <button
      type="button"
      className="bld-btn bld-btn--ghost"
      // Distinct accessible name: the in-canvas preview-mode toggle already exposes the name "Preview".
      aria-label={
        inWorkspace
          ? bt('chrome.top.preview_theme_aria', 'Preview this theme')
          : bt('chrome.top.preview_store_aria', 'Preview your store')
      }
      disabled={!ready}
      title={
        failed
          ? bt('chrome.top.store_retry_hint', 'Couldn’t load your store — click to try again')
          : !ready
            ? bt('chrome.top.store_loading', 'Loading your store…')
            : inWorkspace
              ? btp(
                  'chrome.top.preview_theme_hint',
                  'See {name} on your storefront — your customers still see your live theme',
                  { name: themeName ?? '' },
                )
              : bt('chrome.top.preview_store_hint', 'Preview your live store in a new tab')
      }
      onClick={() => void onClick()}
    >
      <ViewStoreIcon />
      {inWorkspace
        ? bt('chrome.top.preview_theme', 'Preview theme')
        : bt('chrome.top.preview', 'Preview')}
    </button>
  );
}

/**
 * Save actions — the draft is autosaved (debounce 800ms / max-wait 5s), so there is NO "Save draft"
 * button: it was disabled >99% of the time (only live inside the sub-second dirty window) and taught
 * nothing. Its slot now carries the SaveStateIndicator ("Saved · 2m ago" / "Saving…" / "Couldn't save"),
 * which is what the seller actually needs to know. Cmd/Ctrl+S still force-flushes (useBuilderShortcuts),
 * and a "Retry" appears when a save genuinely failed. Publish stays an explicit dark button with its
 * in-app confirm modal (NO native popup).
 */
/**
 * The sentence the failed Retry owes the seller. Each kind names what happened AND what would change it —
 * "try again" is useless advice for a session that expired or a document pointed at another page.
 */
function saveFailureMessage(failure: SaveFailure | null): string {
  switch (failure?.kind) {
    case 'route-mismatch':
      return btp(
        'chrome.save.err_route_mismatch',
        'Your changes belong to another page ({page}) — reopen that page to save them.',
        { page: failure.detail ?? '' },
      );
    case 'session':
      return bt(
        'chrome.save.err_session',
        'Your session has expired — sign in again, your changes are still here.',
      );
    case 'no-store':
      return bt('chrome.save.err_no_store', 'This store is no longer available on your account.');
    case 'store-changed':
      return bt(
        'chrome.save.err_store_changed',
        'Your account switched store — reopen the editor from the store you want to edit.',
      );
    case 'network':
      return bt(
        'chrome.save.err_network',
        'No connection to the server — your changes are kept and will be saved once you are back.',
      );
    case 'server':
      return failure.status
        ? btp(
            'chrome.save.err_server',
            'The server refused the save (error {status}) — your changes are kept.',
            {
              status: failure.status,
            },
          )
        : bt(
            'chrome.save.err_unknown',
            'Couldn’t save — your changes are kept. Try again in a moment.',
          );
    default:
      return bt(
        'chrome.save.err_unknown',
        'Couldn’t save — your changes are kept. Try again in a moment.',
      );
  }
}

function SaveActions() {
  const dirty = useBuilderStore((s) => s.dirty);
  const saveState = useBuilderStore((s) => s.saveState);
  const saveFailure = useBuilderStore((s) => s.saveFailure);
  const hasUnpublishedChanges = useBuilderStore((s) => s.hasUnpublishedChanges);
  // Inside a THEME WORKSPACE (spec 0032) the Publish button publishes the whole theme (pages +
  // colors/typography over the live handle) — the confirm copy must say so, not "this page".
  const routeHandle = useBuilderStore((s) => s.route.handle);
  const workspaceThemeName = isThemeWorkspaceHandle(routeHandle)
    ? (getThemeListing(themeIdOfWorkspaceHandle(routeHandle))?.name ??
      bt('chrome.top.this_theme_lc', 'this theme'))
    : null;

  const [confirmPublish, setConfirmPublish] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // ── Publishing a layout NOTHING renders (founder, 20/09/2026) ────────────────────────────────────
  // "I created a template, I published, and I saw nothing on the store." She was right twice over: the
  // confirm claimed this "replaces what visitors currently see on this page", and the toast said "Your
  // page is live" — both true of the document, both false of her storefront, because no product pointed
  // at the handle. Publishing a named product template that reaches zero products now says so, and
  // offers the missing half of the job: apply it to a product she picks BY NAME.
  const routeTemplate = useBuilderStore((s) => s.route.template);
  const templateUsage = useBuilderStore((s) => s.templateUsage);
  const loadTemplateUsage = useBuilderStore((s) => s.loadTemplateUsage);
  const catalog = useBuilderStore((s) => s.catalog);
  const loadCatalog = useBuilderStore((s) => s.loadCatalog);
  const assignTemplateToProduct = useBuilderStore((s) => s.assignTemplateToProduct);
  const [applyTo, setApplyTo] = useState<string | null>(null);
  const [applyQuery, setApplyQuery] = useState('');
  const [applyError, setApplyError] = useState<string | null>(null);

  /** A named product layout — the only kind that carries a per-product assignment. */
  const namedProductTemplate =
    routeTemplate === 'product' &&
    routeHandle !== 'default' &&
    !isThemeWorkspaceHandle(routeHandle);
  /** Reaches no product. `null` counts are "not loaded", which must never be painted as a zero. */
  const reachesNothing =
    namedProductTemplate && templateUsage !== null && (templateUsage[routeHandle] ?? 0) === 0;
  // ── Publishing a layout the product ON SCREEN does not render (founder, 05/10/2026) ──────────────
  // The other half of the case above: the layout reaches products, just not the one she is looking at.
  // Move Shift on the canvas, `default` open underneath; she switched the buy box to the cart, published,
  // read "Your page is live", and her store kept selling buy-now — the row it serves for that product is
  // `move-shift`. Same rule as the switcher's pill: known-and-different only. The "reaches nothing" case
  // keeps precedence — it carries the one action (pick a product) that fixes it on the spot.
  const previewProduct = useBuilderStore((s) => s.previewProduct);
  const previewProductTemplate = useBuilderStore((s) => s.previewProductTemplate);
  const offProduct =
    routeTemplate === 'product' &&
    !isThemeWorkspaceHandle(routeHandle) &&
    !reachesNothing &&
    previewProduct !== null &&
    previewProductTemplate !== null &&
    previewProductTemplate !== routeHandle;
  const offProductLayout = previewProductTemplate ?? '';

  // Fetch the two things the confirm needs, at the moment it opens — never from a cache filled by some
  // other panel. A stale zero here would nag about a layout she already applied; a stale count would hide
  // the warning entirely, which is the bug this whole block exists to close.
  useEffect(() => {
    if (!confirmPublish || !namedProductTemplate) return;
    void loadTemplateUsage();
    void loadCatalog();
  }, [confirmPublish, namedProductTemplate, loadTemplateUsage, loadCatalog]);

  const saving = saveState === 'saving';
  const readOnlyView = isReadOnlyView();
  const canPublish = (hasUnpublishedChanges || dirty) && !saving && !publishing && !readOnlyView;
  // Only surface a manual save when autosave actually failed — never as a permanently-dead control.
  const canRetrySave = saveState === 'error' || saveState === 'offline';

  /**
   * The Retry click (founder, 2026-09-18: "the Retry button does nothing when I click it").
   *
   * It used to be `onClick={() => void saveDraftNow()}` — fire and forget. Every way a save can fail is
   * painted as the same red "Couldn't save", and the transient 'saving' label is delay-gated, so a retry
   * that failed again in 40ms changed NOTHING on screen: same label, same button, no toast, no console.
   * Worse, some failures can never land on a second attempt (the route-identity refusal never even reaches
   * the network), so the button was genuinely dead and never said so.
   *
   * Now it always answers: saved, or why not — and a failed PUBLISH is retried as a publish, because
   * re-saving the draft would answer "Saved" while the storefront stayed exactly as stale as before.
   */
  async function doRetrySave() {
    if (saveFailure?.reason === 'publish') {
      setRetrying(true);
      await doPublish();
      setRetrying(false);
      return;
    }
    setRetrying(true);
    await saveDraftNow();
    setRetrying(false);
    const st = useBuilderStore.getState();
    if (st.saveState === 'saved' || st.saveState === 'unsaved') {
      pushToast(bt('chrome.save.retry_ok', 'Saved.'), 'success');
      return;
    }
    if (st.saveState === 'conflict') {
      pushToast(
        bt('chrome.save.retry_conflict', 'This page was edited elsewhere — resolve that first.'),
        'error',
      );
      return;
    }
    pushToast(saveFailureMessage(st.saveFailure), 'error');
  }

  async function doPublish() {
    setPublishing(true);
    setApplyError(null);
    const ok = await publish();
    // The chosen product is assigned AFTER the publish, never before: the server refuses a draft handle
    // with 422 INVALID_TEMPLATE_SUFFIX, so the order is not a preference. A publish that failed must not
    // be followed by an assignment either — it would point a live product at a layout still in draft.
    let applied: string | null = null;
    if (ok && applyTo) {
      const target = catalog.find((p) => p.id === applyTo);
      try {
        await assignTemplateToProduct(applyTo, routeHandle);
        applied = target?.name ?? null;
      } catch {
        // Keep the dialog OPEN and say which half succeeded. Closing it on a toast alone would leave her
        // believing the whole job landed — the exact belief that started this.
        setPublishing(false);
        setApplyError(
          bt(
            'chrome.top.publish_applied_failed',
            'Published, but couldn’t apply it to that product. Try applying again.',
          ),
        );
        pushToast(bt('chrome.top.published_not_applied', 'Published — not applied yet.'), 'error');
        return;
      }
    }
    setPublishing(false);
    setConfirmPublish(false);
    setApplyTo(null);
    setApplyQuery('');
    if (ok && applied)
      pushToast(
        btp('chrome.top.published_applied', 'Published — {product} now shows this layout.', {
          product: applied,
        }),
        'success',
      );
    // Honest ending for the case that cost the afternoon: the document IS live, and no visitor can
    // reach it. "Your page is live" would be the third screen in a row telling her the job was done.
    else if (ok && reachesNothing)
      pushToast(
        bt('chrome.top.published_unused', 'Published — but no product shows this layout yet.'),
        'info',
      );
    // Same honesty for the product on screen: the document is live, and that product will not show it.
    else if (ok && offProduct)
      pushToast(
        btp(
          'chrome.top.published_off_product',
          'Published — but {product} shows “{name}”, not this layout.',
          { product: previewProduct?.name ?? '', name: offProductLayout },
        ),
        'info',
      );
    else if (ok) pushToast(bt('chrome.top.published', 'Your page is live.'), 'success');
    else if (useBuilderStore.getState().saveState === 'conflict')
      pushToast(
        bt('chrome.top.publish_conflict', 'Resolve the conflict before publishing.'),
        'error',
      );
    else if (isReadOnlyView())
      pushToast(
        bt(
          'chrome.view.readonly_write',
          'Read-only consultation — nothing can be changed on this store.',
        ),
        'error',
      );
    else pushToast(bt('chrome.top.publish_failed', 'Couldn’t publish. Please try again.'), 'error');
  }

  return (
    <>
      <SaveStateIndicator />
      {/* Always visible, whatever panel is open — the only place that can say a file is still on its way
          after the panel that started it was closed. Renders nothing when nothing is uploading. */}
      <UploadTray />
      {canRetrySave && (
        <button
          type="button"
          className="bld-btn bld-btn--ghost"
          disabled={retrying}
          onClick={() => void doRetrySave()}
        >
          {retrying ? bt('chrome.save.retrying', 'Retrying…') : bt('chrome.common.retry', 'Retry')}
        </button>
      )}
      <button
        type="button"
        className="bld-btn bld-btn--primary"
        disabled={!canPublish}
        title={readOnlyView ? bt('chrome.view.readonly', 'Read-only consultation') : undefined}
        onClick={() => setConfirmPublish(true)}
      >
        {bt('chrome.top.publish', 'Publish')}
      </button>
      {confirmPublish && (
        <Modal
          titleId="bld-publish-title"
          dismissible={!publishing}
          onClose={() => {
            setConfirmPublish(false);
            setApplyTo(null);
            setApplyQuery('');
            setApplyError(null);
          }}
        >
          <h2 id="bld-publish-title" className="bld-modal__title">
            {workspaceThemeName
              ? btp('chrome.top.publish_theme_q', 'Publish {name} to your live storefront?', {
                  name: workspaceThemeName,
                })
              : reachesNothing
                ? // "Publish to your live storefront?" over a body that says publishing changes nothing
                  // is the dialog contradicting itself in two consecutive lines.
                  btp('chrome.top.publish_unused_q', 'Publish “{name}”?', { name: routeHandle })
                : bt('chrome.top.publish_q', 'Publish to your live storefront?')}
          </h2>
          <p className="bld-modal__body">
            {workspaceThemeName
              ? btp(
                  'chrome.top.publish_theme_body',
                  'Your store switches to {name} — its pages, colors and typography replace what visitors currently see.',
                  { name: workspaceThemeName },
                )
              : reachesNothing
                ? // NOT "this replaces what visitors currently see on this page": on a layout no product
                  // renders, that sentence is simply false, and it is the one she read before going to
                  // look for a change that could not be there.
                  btp(
                    'chrome.top.publish_unused_body',
                    'No product uses “{name}”, so publishing it changes nothing for your visitors. Pick the product that should show it:',
                    { name: routeHandle },
                  )
                : offProduct
                  ? // Nor "this replaces what visitors currently see on this page" here: for the product
                    // she is looking at, that sentence is false — the store serves it another layout.
                    btp(
                      'chrome.top.publish_off_product_body',
                      '{product} shows the “{name}” layout, not this one: publishing changes nothing on that product. Open “{name}” to change it, or apply this layout to {product} from the page menu.',
                      { product: previewProduct?.name ?? '', name: offProductLayout },
                    )
                  : bt(
                      'chrome.top.publish_body',
                      'This replaces what visitors currently see on this page with your latest changes.',
                    )}
          </p>

          {/* The missing half of the job, offered where the gap is discovered. One product, named and
              chosen deliberately — not "the one that happens to be in the preview". Skipping is allowed
              and stays honest: the toast then says the layout is published and reaches nobody. */}
          {reachesNothing && (
            <div className="bld-publishapply">
              <div className="bld-pagesel__search">
                <span className="bld-pagesel__searchicon" aria-hidden="true">
                  <SearchIcon size={15} />
                </span>
                <input
                  type="text"
                  className="bld-pagesel__input"
                  placeholder={bt('chrome.prod.search', 'Search products')}
                  aria-label={bt('chrome.prod.search', 'Search products')}
                  value={applyQuery}
                  onChange={(e) => setApplyQuery(e.target.value)}
                />
              </div>
              <ul
                className="bld-pagesel__srclist bld-publishapply__list"
                role="radiogroup"
                aria-label={bt('chrome.top.publish_apply_to', 'Apply to a product')}
              >
                {catalog
                  .filter((p) => p.name.toLowerCase().includes(applyQuery.trim().toLowerCase()))
                  .map((p) => (
                    <li key={p.id} role="none">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={applyTo === p.id}
                        disabled={publishing}
                        className={`bld-pagesel__src${applyTo === p.id ? ' is-selected' : ''}`}
                        // Re-clicking the chosen row clears it: the only way back to "publish without
                        // applying" once a radio group has been touched.
                        onClick={() => setApplyTo((cur) => (cur === p.id ? null : p.id))}
                      >
                        <ProductThumb product={p} />
                        <span className="bld-pagesel__itemlabel">{p.name}</span>
                        {/* `is-selected` is #f1f1f1 on white — all but invisible. Same dot as every
                            other "this one" list in the builder. */}
                        {applyTo === p.id && (
                          <span className="bld-pagesel__nowdot" aria-hidden="true" />
                        )}
                      </button>
                    </li>
                  ))}
                {catalog.length === 0 && (
                  <li className="bld-pagesel__empty">
                    {bt('chrome.prod.none', 'No products yet')}
                  </li>
                )}
              </ul>
              {applyError && (
                <p className="bld-tpldlg__error" role="alert">
                  {applyError}
                </p>
              )}
            </div>
          )}
          <div className="bld-modal__actions">
            <button
              type="button"
              className="bld-btn bld-btn--ghost"
              disabled={publishing}
              onClick={() => {
                setConfirmPublish(false);
                setApplyTo(null);
                setApplyQuery('');
                setApplyError(null);
              }}
            >
              {bt('chrome.common.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              className="bld-btn bld-btn--primary"
              disabled={publishing}
              onClick={() => void doPublish()}
            >
              {/* The label states the whole act. "Publish" over a dialog that is ALSO about to repoint a
                  product page would understate what the click does. */}
              {publishing
                ? bt('chrome.common.publishing', 'Publishing…')
                : applyTo
                  ? bt('chrome.top.publish_and_apply', 'Publish and apply')
                  : reachesNothing || offProduct
                    ? bt('chrome.top.publish_anyway', 'Publish anyway')
                    : bt('chrome.top.publish', 'Publish')}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

/**
 * Builder topbar — Shopify theme-editor menu bar (one thin ~56px white row, hairline bottom border):
 *   LEFT   : sidebar icon-rail (Sections / Theme settings) + exit arrow
 *   CENTER : theme name + Active pill + page/template selector (with a search field)
 *   RIGHT  : inspect/preview toggle · device toggle (desktop|mobile) · undo · redo · "…" more · Save split
 */
export function Topbar() {
  const undo = useBuilderStore((s) => s.undo);
  const redo = useBuilderStore((s) => s.redo);
  const canUndo = useBuilderStore((s) => s.past.length > 0);
  const canRedo = useBuilderStore((s) => s.future.length > 0);

  const previewMode = useBuilderStore((s) => s.previewMode);
  const togglePreviewMode = useBuilderStore((s) => s.togglePreviewMode);

  const mode = useBuilderBreakpoint();

  // MOBILE (<768px) — Shopify mobile menu bar: exit arrow · compact page name · "…" (holds the tabs +
  // undo/redo + inspect toggle) · save state + Publish. The icon-rail, device toggle, undo/redo and the
  // inspect toggle move into the "…" menu (MobileMoreMenu); the save-state indicator hides its calm
  // "Saved · Xm ago" state here (shell.css) so only actionable states compete for the narrow bar.
  if (mode === 'mobile') {
    return (
      <header className="bld-topbar bld-topbar--mobile" role="banner">
        <div className="bld-topbar__zone bld-topbar__zone--left">
          <button
            type="button"
            className="bld-iconbtn-top"
            aria-label={bt('chrome.top.back', 'Back to themes')}
            title={bt('chrome.top.back', 'Back to themes')}
            onClick={() => window.location.assign(dashboardExitUrl())}
          >
            <span className="dir-flip" aria-hidden="true">
              <ArrowLeftIcon />
            </span>
          </button>
          <PageSelector />
        </div>
        <div className="bld-topbar__zone bld-topbar__zone--right">
          <MobileMoreMenu />
          <SaveActions />
        </div>
      </header>
    );
  }

  return (
    <header className="bld-topbar" role="banner">
      <div className="bld-topbar__zone bld-topbar__zone--left">
        <button
          type="button"
          className="bld-iconbtn-top"
          aria-label={bt('chrome.top.back', 'Back to themes')}
          title={bt('chrome.top.back', 'Back to themes')}
          onClick={() => window.location.assign(dashboardExitUrl())}
        >
          <span className="dir-flip" aria-hidden="true">
            <ArrowLeftIcon />
          </span>
        </button>
        <SidebarTabs />
      </div>

      <div className="bld-topbar__zone bld-topbar__zone--center">
        <span className="bld-topbar__theme">
          <span className="bld-topbar__theme-icon" aria-hidden="true">
            <StoreIcon size={16} />
          </span>
          {bt('chrome.top.theme_name', 'Shopiwize theme')}
          <span className="bld-pill bld-pill--active">{bt('chrome.top.active', 'Active')}</span>
        </span>
        <span className="bld-topbar__divider" aria-hidden="true" />
        <PageSelector />
      </div>

      <div className="bld-topbar__zone bld-topbar__zone--right">
        {/* No "Select a component" toggle: hovering the canvas already frames whatever a click will
            select, and clicking a selected section drills into its blocks/fields. The mode toggle was
            one affordance too many for the same job. */}
        <button
          type="button"
          className="bld-iconbtn-top"
          aria-pressed={previewMode}
          aria-label={
            previewMode
              ? bt('chrome.top.exit_preview', 'Exit preview')
              : bt('chrome.top.preview', 'Preview')
          }
          title={
            previewMode
              ? bt('chrome.top.exit_preview', 'Exit preview')
              : bt('chrome.top.preview', 'Preview')
          }
          onClick={() => {
            togglePreviewMode();
            announce(
              useBuilderStore.getState().previewMode
                ? bt(
                    'chrome.top.preview_on_sr',
                    'Preview mode on. Showing your page as visitors see it.',
                  )
                : bt('chrome.top.preview_off_sr', 'Preview mode off. Back to editing.'),
            );
          }}
        >
          <EyeIcon />
        </button>
        <span className="bld-topbar__divider" aria-hidden="true" />
        <DeviceToggle />
        <span className="bld-topbar__divider" aria-hidden="true" />
        <button
          type="button"
          className="bld-iconbtn-top"
          aria-label={bt('chrome.top.undo_aria', 'Undo (Ctrl+Z)')}
          title={bt('chrome.top.undo', 'Undo')}
          aria-disabled={!canUndo}
          disabled={!canUndo}
          onClick={() => undo()}
        >
          <span className="dir-flip" aria-hidden="true">
            <UndoIcon />
          </span>
        </button>
        <button
          type="button"
          className="bld-iconbtn-top"
          aria-label={bt('chrome.top.redo_aria', 'Redo (Ctrl+Shift+Z)')}
          title={bt('chrome.top.redo', 'Redo')}
          aria-disabled={!canRedo}
          disabled={!canRedo}
          onClick={() => redo()}
        >
          <span className="dir-flip" aria-hidden="true">
            <RedoIcon />
          </span>
        </button>
        <MoreMenu />
        <span className="bld-topbar__divider" aria-hidden="true" />
        <PreviewStoreButton />
        <SaveActions />
      </div>
    </header>
  );
}
